import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Plus, Edit2, Trash2, X, Building2, Users, User, Grid3x3, Clock, Wrench, Camera, MapPin, MoreVertical, CheckSquare, ChevronDown, MessageSquare, Eye, List, Bell, CheckCircle2, FileText, Route, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import { plannerService } from '../services/plannerService';
import { kanbanService } from '../services/kanbanService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/userService';

const Planner = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://farm.my-it.pl/api';
  
  // Funkcja pomocnicza do wyświetlania awatara
  const getAvatarUrl = (username) => {
    // Jeśli username pasuje do obecnego użytkownika, zwróć jego awatar
    if (username === user?.username && user?.avatar) {
      // Usuń /api z początku URL jeśli jest, ponieważ statyczne pliki są dostępne bezpośrednio
      const avatarPath = user.avatar.startsWith('/uploads/') ? user.avatar : `/uploads${user.avatar}`;
      // Użyj bezpośrednio farm.my-it.pl bez /api dla statycznych plików
      const baseUrl = process.env.REACT_APP_API_URL 
        ? process.env.REACT_APP_API_URL.replace('/api', '')
        : 'https://farm.my-it.pl';
      return `${baseUrl}${avatarPath}`;
    }
    return null;
  };
  
  // Funkcja pomocnicza do wyświetlania awatara lub inicjału
  const AvatarDisplay = ({ username, size = 'w-8 h-8' }) => {
    const avatarUrl = getAvatarUrl(username);
    return (
      <div className={`${size} rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${
        avatarUrl ? '' : 'bg-gradient-to-br from-gray-200 to-gray-300'
      }`}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={username}
            className={`${size} rounded-full object-cover`}
          />
        ) : (
          <span className="text-xs font-semibold text-gray-700">
            {username?.charAt(0).toUpperCase() || 'U'}
          </span>
        )}
      </div>
    );
  };
  
  // Określ aktywny widok na podstawie URL
  const activeSubmenu = location.pathname.split('/').pop() || 'calendar';
  const [view, setView] = useState('month'); // 'month' or 'week'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [formData, setFormData] = useState({
    farmName: '',
    teamName: '',
    date: '',
    time: '06:00', // Domyślna godzina 6:00
    kanbanCardIds: [] // ID kart z Kanban przypisanych do zadania
  });
  const [teamFormData, setTeamFormData] = useState({
    teamName: '',
    firstServicePerson: '',
    secondServicePerson: '',
    color: '#3B82F6' // Domyślny kolor niebieski
  });
  const [farmFormData, setFarmFormData] = useState({
    farmName: '',
    latitude: '',
    longitude: ''
  });
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showFarmModal, setShowFarmModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingFarm, setEditingFarm] = useState(null);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [selectedTaskForDetails, setSelectedTaskForDetails] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null); // Informacje o trasie (czas, dystans)
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [selectedDateForRoute, setSelectedDateForRoute] = useState(null);
  const routeModalMapRef = useRef(null);
  const routeModalDirectionsRendererRef = useRef(null);
  const routeModalMarkersRef = useRef([]);
  const [routeModalRouteInfo, setRouteModalRouteInfo] = useState(null); // Informacje o trasie w modalu
  const [useManualOrigin, setUseManualOrigin] = useState(false); // Czy używać ręcznie wprowadzonego punktu początkowego
  const [manualOrigin, setManualOrigin] = useState(''); // Ręcznie wprowadzony punkt początkowy
  const [manualOriginPlace, setManualOriginPlace] = useState(null); // Obiekt Place z Google Maps dla punktu początkowego
  const [googleMapsError, setGoogleMapsError] = useState(null); // Błąd ładowania Google Maps
  const originAutocompleteRef = useRef(null);
  const originAutocompleteInstanceRef = useRef(null);
  const manualOriginTimeoutRef = useRef(null); // Ref dla debounce
  const isInitializingRouteRef = useRef(false); // Ref aby zapobiec wielokrotnemu wywoływaniu podczas wpisywania
  const initializeRouteModalMapRef = useRef(null); // Ref do przechowania funkcji initializeRouteModalMap
  
  // React Query dla Kanban - pobieranie list z API
  const { data: kanbanData, isLoading: kanbanLoading, refetch: refetchKanban } = useQuery(
    'kanban-lists',
    async () => {
      const response = await kanbanService.getLists();
      return response.lists || [];
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Błąd pobierania danych Kanban:', error);
        toast.error('Błąd pobierania danych Kanban');
      }
    }
  );

  // Użyj danych z API lub pustej tablicy
  const kanbanLists = kanbanData || [];
  
  // Funkcja do aktualizacji stanu - teraz używa API
  const setKanbanLists = async (lists) => {
    // To jest funkcja pomocnicza, która będzie wywoływana tylko lokalnie
    // Główne zapisywanie odbywa się przez mutacje React Query
    console.log('setKanbanLists called locally (deprecated - use mutations instead)');
  };
  
  // Funkcja pomocnicza do znajdowania karty po ID
  const findCard = (cardId) => {
    if (!cardId) return null;
    
    // Konwertuj cardId na string dla porównania
    const cardIdStr = String(cardId);
    
    for (const list of kanbanLists) {
      const card = list.cards.find(c => {
        // Porównaj jako stringi
        const cIdStr = String(c.id);
        return cIdStr === cardIdStr;
      });
      if (card) {
        console.log('Znaleziono kartę:', { cardId, cardTitle: card.title, listId: list.id });
        return { card, listId: list.id };
      }
    }
    
    console.log('Nie znaleziono karty:', { 
      cardId, 
      cardIdStr,
      availableCards: kanbanLists.flatMap(l => l.cards.map(c => ({ id: c.id, title: c.title, listId: l.id })))
    });
    return null;
  };
  
  // Funkcja pomocnicza do obliczania postępu checklista
  const getChecklistProgress = (checklist) => {
    if (!checklist.items || checklist.items.length === 0) return { completed: 0, total: 0, percent: 0 };
    const completed = checklist.items.filter(item => item.completed).length;
    const total = checklist.items.length;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percent };
  };

  // Funkcja pomocnicza do obliczania postępu zadań w karcie
  const getCardProgress = (card) => {
    if (!card.checklists || card.checklists.length === 0) return null;
    const allItems = card.checklists.flatMap(c => c.items || []);
    if (allItems.length === 0) return null;
    const completed = allItems.filter(item => item.completed).length;
    return { completed, total: allItems.length };
  };
  
  // Funkcja do dodawania aktywności do historii karty
  const addActivity = (card, activityType, activityText) => {
    const activity = {
      id: Date.now().toString(),
      type: activityType,
      text: activityText,
      author: user?.username || 'Użytkownik',
      date: new Date().toISOString()
    };
    
    const updatedCard = {
      ...card,
      activity: [...(card.activity || []), activity]
    };
    
    return updatedCard;
  };
  
  // Funkcja pomocnicza do formatowania daty
  const formatActivityDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'przed chwilą';
    if (diffMins < 60) return `przed ${diffMins} min`;
    if (diffHours < 24) return `przed ${diffHours} h`;
    if (diffDays === 1) return 'wczoraj';
    if (diffDays < 7) return `przed ${diffDays} dniami`;
    
    return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  
  // Funkcja pomocnicza do aktualizacji karty z aktywnością
  const updateCardWithActivity = (card, updates, activityType, activityText) => {
    const updatedCard = addActivity({ ...card, ...updates }, activityType, activityText);
    setSelectedCard(updatedCard);
    
    // Zaktualizuj kartę przez API
    updateCardMutation.mutate({
      id: card.id,
      data: {
        ...updates,
        activities: updatedCard.activities || []
      }
    });
  };

  // Funkcje obsługi drag and drop dla elementów checklistów
  const handleChecklistItemDragStart = (e, item, checklistId, cardId) => {
    setDraggedChecklistItem({
      item: { ...item },
      sourceCardId: cardId,
      sourceChecklistId: checklistId
    });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleChecklistDragOver = (e, checklistId, cardId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverChecklist(checklistId);
    setDragOverCard(cardId);
  };

  const handleChecklistDragLeave = (e) => {
    // Tylko jeśli opuszczamy checklist, nie wewnętrzne elementy
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverChecklist(null);
      setDragOverCard(null);
    }
  };

  const handleChecklistDrop = (e, targetChecklistId, targetCardId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedChecklistItem) {
      setDragOverChecklist(null);
      setDragOverCard(null);
      return;
    }

    const { item, sourceCardId, sourceChecklistId } = draggedChecklistItem;

    // Jeśli element jest upuszczany w tym samym miejscu, nie rób nic
    if (sourceCardId === targetCardId && sourceChecklistId === targetChecklistId) {
      setDraggedChecklistItem(null);
      setDragOverChecklist(null);
      setDragOverCard(null);
      return;
    }

    // Znajdź źródłową kartę i listę
    let sourceCard = null;
    let sourceListId = null;
    for (const list of kanbanLists) {
      sourceCard = list.cards.find(c => c.id === sourceCardId);
      if (sourceCard) {
        sourceListId = list.id;
        break;
      }
    }

    // Znajdź docelową kartę i listę
    let targetCard = null;
    let targetListId = null;
    for (const list of kanbanLists) {
      targetCard = list.cards.find(c => c.id === targetCardId);
      if (targetCard) {
        targetListId = list.id;
        break;
      }
    }

    if (!sourceCard || !targetCard) {
      setDraggedChecklistItem(null);
      setDragOverChecklist(null);
      setDragOverCard(null);
      return;
    }

    // Usuń element ze źródłowego checklista
    const updatedSourceCard = {
      ...sourceCard,
      checklists: (sourceCard.checklists || []).map(c =>
        c.id === sourceChecklistId
          ? {
              ...c,
              items: (c.items || []).filter(i => i.id !== item.id)
            }
          : c
      )
    };

    // Dodaj element do docelowego checklista
    const updatedTargetCard = {
      ...targetCard,
      checklists: (targetCard.checklists || []).map(c =>
        c.id === targetChecklistId
          ? {
              ...c,
              items: [...(c.items || []), item]
            }
          : c
      )
    };

    // Dodaj aktywność do obu kart
    const sourceCardWithActivity = addActivity(updatedSourceCard, 'move', 
      `przeniósł element "${item.title}" z "${sourceCard.title}" do "${targetCard.title}"`
    );
    const targetCardWithActivity = addActivity(updatedTargetCard, 'move', 
      `przeniósł element "${item.title}" z "${sourceCard.title}" do "${targetCard.title}"`
    );

    // Aktualizuj listy
    const updatedLists = kanbanLists.map(list => {
      if (list.id === sourceListId) {
        return {
          ...list,
          cards: list.cards.map(c =>
            c.id === sourceCardId ? sourceCardWithActivity : c
          )
        };
      }
      if (list.id === targetListId) {
        return {
          ...list,
          cards: list.cards.map(c =>
            c.id === targetCardId ? targetCardWithActivity : c
          )
        };
      }
      return list;
    });

    setKanbanLists(updatedLists);

    // Jeśli otwarta karta była źródłem lub celem, zaktualizuj ją
    if (selectedCard && (selectedCard.id === sourceCardId || selectedCard.id === targetCardId)) {
      if (selectedCard.id === sourceCardId) {
        setSelectedCard(sourceCardWithActivity);
      } else {
        setSelectedCard(targetCardWithActivity);
      }
    }

    setDraggedChecklistItem(null);
    setDragOverChecklist(null);
    setDragOverCard(null);
    toast.success(`Element przeniesiony do "${targetCard.title}"`);
  };

  const handleChecklistDragEnd = () => {
    setDraggedChecklistItem(null);
    setDragOverChecklist(null);
    setDragOverCard(null);
  };

  // Funkcje obsługi drag and drop dla kart Kanban między listami
  const handleKanbanCardDragStart = (e, card, sourceListId) => {
    setDraggedKanbanCard({
      card: { ...card },
      sourceListId: sourceListId
    });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleKanbanCardDragEnd = () => {
    setDraggedKanbanCard(null);
    setDragOverList(null);
  };

  const handleKanbanListDragOver = (e, listId) => {
    e.preventDefault();
    e.stopPropagation();
    // Tylko jeśli przeciągamy kartę, nie element checklista
    if (draggedKanbanCard && !draggedChecklistItem) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverList(listId);
    }
  };

  const handleKanbanListDragLeave = (e) => {
    // Tylko jeśli opuszczamy listę, nie wewnętrzne elementy
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverList(null);
    }
  };

  const handleKanbanListDrop = (e, targetListId) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Tylko jeśli przeciągamy kartę, nie element checklista
    if (!draggedKanbanCard || draggedChecklistItem) {
      setDragOverList(null);
      return;
    }

    const { card, sourceListId } = draggedKanbanCard;

    // Jeśli karta jest upuszczana w tej samej liście, nie rób nic
    if (sourceListId === targetListId) {
      setDraggedKanbanCard(null);
      setDragOverList(null);
      return;
    }

    // Znajdź tytuł docelowej listy
    const targetList = kanbanLists.find(l => l.id === targetListId);
    const targetListTitle = targetList?.title || '';

    // Przygotuj zaktualizowaną kartę z aktywnością
    const updatedActivities = [
      ...(card.activities || []),
      {
        id: Date.now().toString(),
        type: 'move',
        text: `przeniósł kartę do "${targetListTitle}"`,
        author: user?.username || user?.email || 'Użytkownik',
        createdAt: new Date().toISOString()
      }
    ];

    // Zaktualizuj kartę przez API
    updateCardMutation.mutate({
      id: card.id,
      data: {
        listId: targetListId,
        activities: updatedActivities
      }
    });

    // Jeśli otwarta karta była przenoszona, zaktualizuj ją lokalnie
    if (selectedCard && selectedCard.id === card.id) {
      const updatedCard = {
        ...card,
        activities: updatedActivities
      };
      setSelectedCard(updatedCard);
      setSelectedCardListId(targetListId);
    }

    setDraggedKanbanCard(null);
    setDragOverList(null);
    toast.success(`Karta przeniesiona do "${targetListTitle}"`);
  };
  const [showAddListModal, setShowAddListModal] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [selectedListId, setSelectedListId] = useState(null);
  const [newCardTitle, setNewCardTitle] = useState('');
  const [showListMenu, setShowListMenu] = useState(null); // ID listy dla której pokazane jest menu
  const [selectedCard, setSelectedCard] = useState(null); // Karta dla której otwarty jest modal szczegółów
  const [selectedCardListId, setSelectedCardListId] = useState(null); // ID listy dla wybranej karty
  const [newTaskTitle, setNewTaskTitle] = useState(''); // Tytuł nowego zadania w checkliście
  const [showListDropdown, setShowListDropdown] = useState(false); // Czy pokazać dropdown z listami
  const [editingDescription, setEditingDescription] = useState(false); // Czy edytujemy opis
  const [descriptionText, setDescriptionText] = useState(''); // Tekst opisu
  const [newComment, setNewComment] = useState(''); // Nowy komentarz
  const [newChecklistTitle, setNewChecklistTitle] = useState(''); // Tytuł nowego checklista
  const [newChecklistItemTitle, setNewChecklistItemTitle] = useState({}); // Tytuły nowych elementów checklista {checklistId: title}
  const [hiddenChecklists, setHiddenChecklists] = useState({}); // Ukryte checklisty {checklistId: true}
  const [draggedChecklistItem, setDraggedChecklistItem] = useState(null); // Przeciągany element checklista {item, sourceCardId, sourceChecklistId}
  const [dragOverChecklist, setDragOverChecklist] = useState(null); // ID checklista nad którym jest przeciągany element
  const [dragOverCard, setDragOverCard] = useState(null); // ID karty nad którą jest przeciągany element
  const [draggedKanbanCard, setDraggedKanbanCard] = useState(null); // Przeciągana karta {card, sourceListId}
  const [dragOverList, setDragOverList] = useState(null); // ID listy nad którą jest przeciągana karta
  const [editingCardTitleInModal, setEditingCardTitleInModal] = useState(false); // Czy edytujemy tytuł w modalu
  const [editingCardTitleTextInModal, setEditingCardTitleTextInModal] = useState(''); // Tekst edytowanego tytułu w modalu
  const [editingListTitle, setEditingListTitle] = useState(null); // ID listy której tytuł edytujemy
  const [editingListTitleText, setEditingListTitleText] = useState(''); // Tekst edytowanego tytułu listy
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  // Refy dla mapy w szczegółach zadania
  const taskDetailsMapRef = useRef(null);
  const taskDetailsMapInstanceRef = useRef(null);
  const taskDetailsMarkersRef = useRef([]);
  const taskDetailsDirectionsRendererRef = useRef(null);
  const queryClient = useQueryClient();

  // Oblicz zakres dat w zależności od widoku
  const dateRange = useMemo(() => {
    if (view === 'week') {
      // Tydzień od poniedziałku do niedzieli
      const monday = new Date(currentWeek);
      const day = monday.getDay();
      const diff = monday.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
      const startOfWeek = new Date(monday.setDate(diff));
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      
      return { start: startOfWeek, end: endOfWeek };
    } else {
      const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      
      return { start: startOfMonth, end: endOfMonth };
    }
  }, [view, currentMonth, currentWeek]);

  const { data: tasksData, isLoading } = useQuery(
    ['planner-tasks', dateRange.start.toISOString(), dateRange.end.toISOString()],
    () => plannerService.getTasks(dateRange.start.toISOString(), dateRange.end.toISOString())
  );

  const tasks = tasksData?.tasks || [];

  // Automatyczne czyszczenie starych przypisań kart Kanban z zadań
  useEffect(() => {
    if (tasks.length > 0 && kanbanLists.length > 0 && (user?.role === 'super admin' || user?.role === 'admin')) {
      // Sprawdź wszystkie zadania i znajdź te ze starymi przypisaniami
      const tasksToUpdate = tasks.filter(task => {
        if (!task.kanbanCardIds || task.kanbanCardIds.length === 0) return false;
        
        // Sprawdź czy jakieś karty nie istnieją w aktualnym Kanban
        const hasInvalidCards = task.kanbanCardIds.some(cardId => {
          const existsInApi = kanbanLists.some(list => 
            list.cards.some(card => String(card.id) === String(cardId))
          );
          return !existsInApi;
        });
        
        return hasInvalidCards;
      });

      // Jeśli są zadania do aktualizacji, wyświetl informację i zaktualizuj je
      if (tasksToUpdate.length > 0) {
        console.log(`Znaleziono ${tasksToUpdate.length} zadań ze starymi przypisaniami kart Kanban`);
        
        // Zaktualizuj wszystkie zadania ze starymi kartami
        tasksToUpdate.forEach(task => {
          const validKanbanCardIds = (task.kanbanCardIds || []).filter(cardId => {
            const existsInApi = kanbanLists.some(list => 
              list.cards.some(card => String(card.id) === String(cardId))
            );
            return existsInApi;
          });
          
          if (validKanbanCardIds.length !== (task.kanbanCardIds || []).length) {
            const updatedKanbanCards = (task.kanbanCards || []).filter(card => 
              validKanbanCardIds.includes(card.cardId)
            );
            
            // Zaktualizuj zadanie w tle
            updateTaskMutation.mutate({
              id: task._id,
              data: {
                kanbanCardIds: validKanbanCardIds,
                kanbanCards: updatedKanbanCards
              }
            }, {
              onSuccess: () => {
                console.log(`Zaktualizowano zadanie ${task._id} - usunięto stare karty Kanban`);
              }
            });
          }
        });
      }
    }
  }, [tasks, kanbanLists, user]);

  // Mutacja do dodawania zadania
  const addTaskMutation = useMutation(plannerService.addTask, {
    onSuccess: () => {
      queryClient.invalidateQueries('planner-tasks');
      toast.success('Zadanie zostało dodane');
      setShowModal(false);
      resetForm();
    },
    onError: (error) => {
      console.error('Błąd podczas dodawania zadania:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas dodawania zadania';
      toast.error(errorMessage);
    }
  });

  // Mutacja do aktualizacji zadania
  const updateTaskMutation = useMutation(
    ({ id, data }) => plannerService.updateTask(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('planner-tasks');
        toast.success('Zadanie zostało zaktualizowane');
        setShowModal(false);
        setEditingTask(null);
        resetForm();
      },
      onError: (error) => {
        console.error('Błąd podczas aktualizacji zadania:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas aktualizacji zadania';
        toast.error(errorMessage);
      }
    }
  );

  // Mutacja do usuwania zadania
  const deleteTaskMutation = useMutation(plannerService.deleteTask, {
    onSuccess: () => {
      queryClient.invalidateQueries('planner-tasks');
      toast.success('Zadanie zostało usunięte');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania zadania');
    }
  });

  // Mutacja do przesuwania zadania (zmiana daty)
  const moveTaskMutation = useMutation(
    ({ id, newDate }) => plannerService.updateTask(id, { date: newDate }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('planner-tasks');
        toast.success('Zadanie zostało przeniesione');
        setDraggedTask(null);
        setDragOverDay(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas przenoszenia zadania');
        setDraggedTask(null);
        setDragOverDay(null);
      }
    }
  );

  // Mutacja do oznaczania realizacji zadania
  const completeTaskMutation = useMutation(
    ({ taskId, kanbanCardId }) => plannerService.completeTask(taskId, kanbanCardId),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('planner-tasks');
        toast.success('Realizacja zadania została zaktualizowana');
        
        // Zaktualizuj zadanie w modalu szczegółów jeśli jest otwarty
        if (selectedTaskForDetails && selectedTaskForDetails._id === response?.task?._id) {
          setSelectedTaskForDetails(response.task);
        }
      },
      onError: (error) => {
        console.error('Błąd podczas oznaczania realizacji zadania:', error);
        toast.error(error.response?.data?.message || 'Błąd podczas oznaczania realizacji zadania');
      }
    }
  );

  // Mutacja do oznaczania elementu checklista
  const completeChecklistItemMutation = useMutation(
    ({ taskId, kanbanCardId, checklistId, itemId }) => plannerService.completeChecklistItem(taskId, kanbanCardId, checklistId, itemId),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('planner-tasks');
        
        // Zaktualizuj zadanie w modalu szczegółów jeśli jest otwarty
        if (selectedTaskForDetails && selectedTaskForDetails._id === response?.task?._id) {
          setSelectedTaskForDetails(response.task);
        }
      },
      onError: (error) => {
        console.error('Błąd podczas oznaczania elementu checklista:', error);
        toast.error(error.response?.data?.message || 'Błąd podczas oznaczania elementu checklista');
      }
    }
  );

  // Mutacje dla Kanban
  const createListMutation = useMutation(kanbanService.createList, {
    onSuccess: () => {
      queryClient.invalidateQueries('kanban-lists');
      toast.success('Lista została utworzona');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas tworzenia listy');
    }
  });

  const updateListMutation = useMutation(
    ({ id, data }) => kanbanService.updateList(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kanban-lists');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas aktualizacji listy');
      }
    }
  );

  const deleteListMutation = useMutation(kanbanService.deleteList, {
    onSuccess: () => {
      queryClient.invalidateQueries('kanban-lists');
      toast.success('Lista została usunięta');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania listy');
    }
  });

  const createCardMutation = useMutation(kanbanService.createCard, {
    onSuccess: () => {
      queryClient.invalidateQueries('kanban-lists');
      toast.success('Karta została utworzona');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas tworzenia karty');
    }
  });

  const updateCardMutation = useMutation(
    ({ id, data }) => kanbanService.updateCard(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kanban-lists');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas aktualizacji karty');
      }
    }
  );

  const deleteCardMutation = useMutation(kanbanService.deleteCard, {
    onSuccess: () => {
      queryClient.invalidateQueries('kanban-lists');
      toast.success('Karta została usunięta');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania karty');
    }
  });

  // Funkcja pomocnicza do sprawdzania czy element checklista jest oznaczony przez użytkownika
  const isChecklistItemCompletedByUser = (task, kanbanCardId, checklistId, itemId) => {
    if (!user || !user._id || !task.checklistItemCompletions) return false;
    
    const userIdStr = user._id.toString();
    
    return task.checklistItemCompletions.some(completion => {
      const completionUserId = completion.userId?._id?.toString() || completion.userId?.toString() || completion.userId;
      return completionUserId === userIdStr &&
             String(completion.kanbanCardId) === String(kanbanCardId) &&
             String(completion.checklistId) === String(checklistId) &&
             String(completion.itemId) === String(itemId);
    });
  };

  // Funkcja pomocnicza do sprawdzania czy użytkownik należy do ekipy
  const isUserInTeam = (teamName) => {
    if (!user || !teamName || !user._id) {
      console.log('isUserInTeam: brak user lub teamName lub user._id', { user: user?.username, teamName, hasUserId: !!user?._id });
      return false;
    }
    
    const team = teams.find(t => t.teamName === teamName);
    if (!team) {
      console.log('isUserInTeam: ekipa nie znaleziona', { teamName, availableTeams: teams.map(t => t.teamName) });
      return false;
    }
    
    const firstPersonId = team.firstServicePerson?._id?.toString() || team.firstServicePerson?.toString();
    const secondPersonId = team.secondServicePerson?._id?.toString() || team.secondServicePerson?.toString();
    const userId = user._id.toString();
    
    console.log('isUserInTeam: sprawdzanie członkostwa', {
      teamName,
      userId,
      firstPersonId,
      secondPersonId,
      firstMatch: firstPersonId === userId,
      secondMatch: secondPersonId === userId,
      teamData: {
        firstPerson: team.firstServicePerson,
        secondPerson: team.secondServicePerson
      }
    });
    
    const isMember = firstPersonId === userId || secondPersonId === userId;
    return isMember;
  };

  // Funkcja pomocnicza do sprawdzania czy zadanie jest oznaczone jako wykonane przez użytkownika
  const isTaskCompletedByUser = (task, kanbanCardId) => {
    if (!task.taskCompletions || !user || !user._id) return false;
    const userIdStr = user._id.toString();
    return task.taskCompletions.some(completion => {
      const completionUserId = completion.userId?._id?.toString() || completion.userId?.toString() || completion.userId;
      return completionUserId === userIdStr && completion.kanbanCardId === kanbanCardId;
    });
  };

  // Query dla ekip serwisowych - zawsze włączone, aby kolory były dostępne
  const { data: teamsData, isLoading: teamsLoading } = useQuery(
    'planner-teams',
    plannerService.getTeams,
    { 
      enabled: true, // Zawsze włączone, aby mieć dostęp do kolorów ekip
      refetchOnMount: true, // Zawsze pobierz świeże dane przy montowaniu
      refetchOnWindowFocus: true // Pobierz świeże dane przy powrocie do okna
    }
  );

  const teams = teamsData?.teams || [];

  // Debug: loguj ekipy i użytkownika
  useEffect(() => {
    if (teams.length > 0 && user) {
      console.log('Ekipy załadowane:', teams);
      console.log('Użytkownik:', { id: user._id, username: user.username });
      teams.forEach(team => {
        console.log(`Ekipa ${team.teamName}:`, {
          firstPerson: team.firstServicePerson,
          secondPerson: team.secondServicePerson
        });
      });
    }
  }, [teams, user]);

  // Query dla farm
  const { data: farmsData, isLoading: farmsLoading, error: farmsError } = useQuery(
    'planner-farms',
    plannerService.getFarms,
    { 
      enabled: activeSubmenu === 'farms' || activeSubmenu === 'map' || activeSubmenu === 'calendar',
      refetchOnMount: true, // Zawsze pobierz świeże dane przy montowaniu
      refetchOnWindowFocus: true, // Pobierz świeże dane przy powrocie do okna
      staleTime: 0, // Dane są nieaktualne natychmiast
      cacheTime: 0, // Nie cache'uj danych
      onSuccess: (data) => {
        console.log('Farmy pobrane z API:', data);
        console.log('Liczba farm:', data?.farms?.length || 0);
        console.log('Typ farms:', typeof data?.farms);
        console.log('Czy farms jest tablicą:', Array.isArray(data?.farms));
      },
      onError: (error) => {
        console.error('Błąd pobierania farm:', error);
        console.error('Status:', error.response?.status);
        console.error('Data:', error.response?.data);
      }
    }
  );

  const farms = farmsData?.farms || [];

  // Debug: loguj activeSubmenu i enabled state
  useEffect(() => {
    console.log('[DEBUG] activeSubmenu:', activeSubmenu);
    console.log('[DEBUG] Query enabled:', activeSubmenu === 'farms' || activeSubmenu === 'map' || activeSubmenu === 'calendar');
    console.log('[DEBUG] farmsLoading:', farmsLoading);
    console.log('[DEBUG] farmsError:', farmsError);
    console.log('[DEBUG] farms:', farms);
    console.log('[DEBUG] farms.length:', farms.length);
  }, [activeSubmenu, farmsLoading, farmsError, farms]);

  // Query dla użytkowników (do wyboru w ekipach i wyświetlania)
  const { data: usersData, isLoading: usersLoading } = useQuery(
    'users',
    userService.getUsers,
    {
      enabled: activeSubmenu === 'teams' || showTeamModal || activeSubmenu === 'calendar',
      staleTime: 5 * 60 * 1000, // Cache na 5 minut
    }
  );

  const allUsers = usersData?.users || [];

  // Mutacje dla ekip serwisowych
  const addTeamMutation = useMutation(plannerService.addTeam, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('planner-teams');
      toast.success('Ekipa została dodana');
      setShowTeamModal(false);
      resetTeamForm();
      // Jeśli był callback, wykonaj go
      if (window._plannerTeamCallback) {
        const callback = window._plannerTeamCallback;
        window._plannerTeamCallback = null;
        if (data?.team?.teamName) {
          callback(data.team.teamName);
        }
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas dodawania ekipy');
    }
  });

  const updateTeamMutation = useMutation(
    ({ id, data }) => plannerService.updateTeam(id, data),
    {
      onSuccess: (response) => {
        // Debug log
        console.log('Update team response:', response);
        console.log('Team color from response:', response?.team?.color);
        
        // Bezpośrednio zaktualizuj cache z zaktualizowaną ekipą
        if (response?.team) {
          queryClient.setQueryData('planner-teams', (oldData) => {
            if (!oldData) {
              console.log('No old data in cache');
              return oldData;
            }
            console.log('Old teams data:', oldData.teams);
            const updatedData = {
              ...oldData,
              teams: oldData.teams.map(team => {
                // Porównaj ID jako stringi dla bezpieczeństwa
                const teamId = team._id?.toString ? team._id.toString() : team._id;
                const responseTeamId = response.team._id?.toString ? response.team._id.toString() : response.team._id;
                const isMatch = teamId === responseTeamId;
                if (isMatch) {
                  console.log('Updating team in cache:', team.teamName, 'from color', team.color, 'to', response.team.color);
                }
                return isMatch ? response.team : team;
              })
            };
            console.log('Updated teams data:', updatedData.teams);
            return updatedData;
          });
        }
        // Również invaliduj i wymuś refetch
        queryClient.invalidateQueries('planner-teams');
        queryClient.refetchQueries('planner-teams');
        // Odśwież zadania, aby używały nowego koloru ekipy
        queryClient.invalidateQueries('planner-tasks');
        toast.success('Ekipa została zaktualizowana');
        setShowTeamModal(false);
        setEditingTeam(null);
        resetTeamForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas aktualizacji ekipy');
      }
    }
  );

  const deleteTeamMutation = useMutation(plannerService.deleteTeam, {
    onSuccess: () => {
      queryClient.invalidateQueries('planner-teams');
      toast.success('Ekipa została usunięta');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania ekipy');
    }
  });

  // Mutacje dla farm
  const addFarmMutation = useMutation(plannerService.addFarm, {
    onSuccess: (data) => {
      queryClient.invalidateQueries('planner-farms');
      toast.success('Farma została dodana');
      setShowFarmModal(false);
      resetFarmForm();
      // Jeśli był callback, wykonaj go
      if (window._plannerFarmCallback) {
        const callback = window._plannerFarmCallback;
        window._plannerFarmCallback = null;
        if (data?.farm?.farmName) {
          callback(data.farm.farmName);
        }
      }
      // Odśwież mapę po dodaniu farmy
      setTimeout(() => {
        if (activeSubmenu === 'map' && mapLoaded && window.google && window.google.maps && window.google.maps.Map) {
          initializeMap();
        }
      }, 300);
    },
    onError: (error) => {
      console.error('Błąd dodawania farmy:', error);
      toast.error(error.response?.data?.message || 'Błąd podczas dodawania farmy');
    }
  });

  const updateFarmMutation = useMutation(
    ({ id, data }) => plannerService.updateFarm(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('planner-farms');
        toast.success('Farma została zaktualizowana');
        setShowFarmModal(false);
        setEditingFarm(null);
        resetFarmForm();
        if (activeSubmenu === 'map' && window.google && window.google.maps && window.google.maps.Map) {
          setTimeout(initializeMap, 500);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas aktualizacji farmy');
      }
    }
  );

  const deleteFarmMutation = useMutation(plannerService.deleteFarm, {
    onSuccess: () => {
      queryClient.invalidateQueries('planner-farms');
      toast.success('Farma została usunięta');
      if (activeSubmenu === 'map') {
        setTimeout(initializeMap, 500);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania farmy');
    }
  });

  // Funkcje pomocnicze do resetowania formularzy
  const resetTeamForm = () => {
    setTeamFormData({
      teamName: '',
      firstServicePerson: '',
      secondServicePerson: '',
      color: '#3B82F6'
    });
  };

  const resetFarmForm = () => {
    setFarmFormData({
      farmName: '',
      latitude: '',
      longitude: ''
    });
  };

  // Funkcje do zarządzania mapą Google Maps
  useEffect(() => {
    if (activeSubmenu === 'map') {
      const initializeMapWhenReady = () => {
        // Sprawdź czy ref jest dostępny
        if (!mapRef.current) {
          setTimeout(initializeMapWhenReady, 50);
          return;
        }

        // Sprawdź czy Google Maps API jest już załadowane
        if (window.google && window.google.maps && window.google.maps.Map) {
          // Google Maps już załadowany
          if (!mapLoaded) {
            setMapLoaded(true);
          }
          setTimeout(initializeMap, 300);
          return;
        }

        // Sprawdź czy skrypt już jest ładowany
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          // Skrypt już się ładuje, poczekaj aż się załaduje
          if (!existingScript.onload) {
            existingScript.onload = () => {
              setMapLoaded(true);
              setTimeout(initializeMap, 300);
            };
          }
          return;
        }

        // Załaduj skrypt Google Maps
        if (!mapLoaded) {
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY'}&libraries=places,directions`;
          script.async = true;
          script.defer = true;
          script.onload = () => {
            setMapLoaded(true);
            setTimeout(initializeMap, 300);
          };
          script.onerror = () => {
            console.error('Błąd ładowania Google Maps API');
          };
          document.head.appendChild(script);
        }
      };

      initializeMapWhenReady();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubmenu, farms.length, mapLoaded]);

  const initializeMap = () => {
    // Sprawdź czy Google Maps API jest w pełni załadowane
    if (!window.google || !window.google.maps || !window.google.maps.Map) {
      console.log('Oczekiwanie na pełne załadowanie Google Maps API...');
      setTimeout(initializeMap, 100);
      return;
    }

    if (!mapRef.current) {
      console.log('Ref mapy nie jest dostępny');
      return;
    }

    try {
      // Jeśli mapa już istnieje, wyczyść stare markery
      if (markersRef.current.length > 0) {
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
      }

      // Jeśli mapa już istnieje, użyj jej, w przeciwnym razie utwórz nową
      if (!mapInstanceRef.current) {
        const center = { lat: 52.237049, lng: 21.017532 };
        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
          zoom: 6,
          center: center,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP
        });
      }

      const map = mapInstanceRef.current;

      // Debug: sprawdź ile farm jest dostępnych
      console.log('Ładowanie farm na mapę:', farms.length, 'farm');
      console.log('Farmy:', farms);

      // Dodaj znaczniki dla wszystkich farm
      let markersAdded = 0;
      farms.forEach(farm => {
        if (farm.location && farm.location.latitude && farm.location.longitude) {
          try {
            const marker = new window.google.maps.Marker({
              position: {
                lat: farm.location.latitude,
                lng: farm.location.longitude
              },
              map: map,
              title: farm.farmName
            });

            const infoWindow = new window.google.maps.InfoWindow({
              content: `<div><strong>${farm.farmName}</strong><br/>Lat: ${farm.location.latitude.toFixed(6)}, Lng: ${farm.location.longitude.toFixed(6)}</div>`
            });

            marker.addListener('click', () => {
              infoWindow.open(map, marker);
            });

            markersRef.current.push(marker);
            markersAdded++;
          } catch (error) {
            console.error(`Błąd dodawania markera dla farmy ${farm.farmName}:`, error);
          }
        } else {
          console.log(`Farma ${farm.farmName} nie ma lokalizacji GPS`);
        }
      });

      console.log(`Dodano ${markersAdded} markerów na mapę`);
    } catch (error) {
      console.error('Błąd inicjalizacji mapy:', error);
    }
  };

  // Inicjalizacja mapy w szczegółach zadania
  const initializeTaskDetailsMap = () => {
    // Sprawdź czy Google Maps API jest w pełni załadowane
    if (!window.google || !window.google.maps || !window.google.maps.Map) {
      console.log('Oczekiwanie na pełne załadowanie Google Maps API...');
      setTimeout(initializeTaskDetailsMap, 100);
      return;
    }

    if (!taskDetailsMapRef.current || !selectedTaskForDetails) {
      console.log('Ref mapy lub zadanie nie jest dostępne');
      return;
    }

    // Wyczyść stare markery i trasę
    if (taskDetailsMarkersRef.current.length > 0) {
      taskDetailsMarkersRef.current.forEach(marker => marker.setMap(null));
      taskDetailsMarkersRef.current = [];
    }
    if (taskDetailsDirectionsRendererRef.current) {
      taskDetailsDirectionsRendererRef.current.setMap(null);
      taskDetailsDirectionsRendererRef.current = null;
    }
    setRouteInfo(null); // Wyczyść informacje o trasie

    // Znajdź farmę przypisaną do zadania
    const currentFarm = farms.find(f => f.farmName === selectedTaskForDetails.farmName);
    if (!currentFarm || !currentFarm.location) {
      console.log('Nie znaleziono farmy lub brak współrzędnych');
      return;
    }

    try {
      // Utwórz lub zaktualizuj mapę
      if (!taskDetailsMapInstanceRef.current) {
        taskDetailsMapInstanceRef.current = new window.google.maps.Map(taskDetailsMapRef.current, {
          zoom: 10,
          center: { lat: currentFarm.location.latitude, lng: currentFarm.location.longitude },
          mapTypeId: window.google.maps.MapTypeId.ROADMAP
        });
      } else {
        taskDetailsMapInstanceRef.current.setCenter({ lat: currentFarm.location.latitude, lng: currentFarm.location.longitude });
        taskDetailsMapInstanceRef.current.setZoom(10);
      }

      const map = taskDetailsMapInstanceRef.current;

      // Znajdź wszystkie zadania tego samego dnia dla tej samej ekipy
      const taskDate = new Date(selectedTaskForDetails.date);
      const taskDateStart = new Date(taskDate);
      taskDateStart.setHours(0, 0, 0, 0);
      const taskDateEnd = new Date(taskDate);
      taskDateEnd.setHours(23, 59, 59, 999);

      const sameDayTasks = tasks.filter(task => {
        const taskDate = new Date(task.date);
        return task.teamName === selectedTaskForDetails.teamName &&
               taskDate >= taskDateStart &&
               taskDate <= taskDateEnd &&
               task._id !== selectedTaskForDetails._id;
      });

      // Znajdź farmy dla wszystkich zadań tego dnia
      const farmLocations = [];
      
      // Dodaj bieżącą farmę jako pierwszą
      farmLocations.push({
        farm: currentFarm,
        task: selectedTaskForDetails,
        isCurrent: true
      });

      // Dodaj inne farmy tego dnia
      sameDayTasks.forEach(task => {
        const farm = farms.find(f => f.farmName === task.farmName);
        if (farm && farm.location) {
          farmLocations.push({
            farm: farm,
            task: task,
            isCurrent: false
          });
        }
      });

      // Dodaj markery dla wszystkich farm
      farmLocations.forEach((item, index) => {
        const marker = new window.google.maps.Marker({
          position: { lat: item.farm.location.latitude, lng: item.farm.location.longitude },
          map: map,
          title: item.farm.farmName,
          icon: item.isCurrent ? {
            url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
          } : {
            url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
          },
          label: {
            text: String(index + 1),
            color: 'white',
            fontWeight: 'bold'
          }
        });

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div>
              <strong>${item.farm.farmName}</strong><br/>
              Ekipa: ${item.task.teamName}<br/>
              Godzina: ${new Date(item.task.date).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

        taskDetailsMarkersRef.current.push(marker);
      });

      // Jeśli są co najmniej 2 farmy, narysuj trasę
      if (farmLocations.length >= 2 && window.google.maps.DirectionsService) {
        const directionsService = new window.google.maps.DirectionsService();
        const directionsRenderer = new window.google.maps.DirectionsRenderer({
          map: map,
          suppressMarkers: true // Używamy własnych markerów
        });

        taskDetailsDirectionsRendererRef.current = directionsRenderer;

        // Przygotuj waypoints (wszystkie farmy oprócz pierwszej i ostatniej)
        const waypoints = farmLocations.slice(1, -1).map(item => ({
          location: { lat: item.farm.location.latitude, lng: item.farm.location.longitude },
          stopover: true
        }));

        const request = {
          origin: { lat: farmLocations[0].farm.location.latitude, lng: farmLocations[0].farm.location.longitude },
          destination: { lat: farmLocations[farmLocations.length - 1].farm.location.latitude, lng: farmLocations[farmLocations.length - 1].farm.location.longitude },
          waypoints: waypoints,
          optimizeWaypoints: true,
          travelMode: window.google.maps.TravelMode.DRIVING
        };

        directionsService.route(request, (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            directionsRenderer.setDirections(result);
            
            // Zapisz informacje o trasie
            let totalDistance = 0;
            let totalDuration = 0;
            
            result.routes[0].legs.forEach(leg => {
              totalDistance += leg.distance.value; // w metrach
              totalDuration += leg.duration.value; // w sekundach
            });
            
            const distanceKm = (totalDistance / 1000).toFixed(1);
            const durationHours = Math.floor(totalDuration / 3600);
            const durationMinutes = Math.floor((totalDuration % 3600) / 60);
            
            setRouteInfo({
              distance: distanceKm,
              duration: durationHours > 0 
                ? `${durationHours}h ${durationMinutes}min`
                : `${durationMinutes}min`,
              durationSeconds: totalDuration
            });
          } else {
            console.error('Błąd podczas pobierania trasy:', status);
            setRouteInfo(null);
          }
        });
      }

      // Dostosuj zoom aby pokazać wszystkie farmy
      if (farmLocations.length > 1 && window.google.maps.LatLngBounds) {
        const bounds = new window.google.maps.LatLngBounds();
        farmLocations.forEach(item => {
          bounds.extend({ lat: item.farm.location.latitude, lng: item.farm.location.longitude });
        });
        map.fitBounds(bounds);
      }
    } catch (error) {
      console.error('Błąd inicjalizacji mapy:', error);
    }
  };

  // Załaduj Google Maps API i zainicjalizuj mapę szczegółów zadania
  useEffect(() => {
    if (showTaskDetailsModal && selectedTaskForDetails && farms.length > 0) {
      const initializeMapWhenReady = () => {
        // Sprawdź czy ref jest już dostępny
        if (!taskDetailsMapRef.current) {
          // Jeśli ref jeszcze nie jest dostępny, poczekaj chwilę
          setTimeout(initializeMapWhenReady, 50);
          return;
        }

        // Sprawdź czy Google Maps API jest już załadowane
        if (window.google && window.google.maps && window.google.maps.Map) {
          // Google Maps już załadowany, poczekaj chwilę i zainicjalizuj
          setTimeout(() => {
            initializeTaskDetailsMap();
          }, 300);
          return;
        }

        // Sprawdź czy skrypt już jest ładowany
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          // Skrypt już się ładuje, poczekaj aż się załaduje
          existingScript.onload = () => {
            setTimeout(() => {
              initializeTaskDetailsMap();
            }, 300);
          };
          return;
        }

        // Załaduj skrypt Google Maps
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY'}&libraries=places,directions`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          setTimeout(() => {
            initializeTaskDetailsMap();
          }, 300);
        };
        script.onerror = () => {
          console.error('Błąd ładowania Google Maps API');
        };
        document.head.appendChild(script);
      };

      initializeMapWhenReady();
    } else {
      // Wyczyść mapę gdy modal jest zamknięty
      if (taskDetailsMarkersRef.current.length > 0) {
        taskDetailsMarkersRef.current.forEach(marker => marker.setMap(null));
        taskDetailsMarkersRef.current = [];
      }
      if (taskDetailsDirectionsRendererRef.current) {
        taskDetailsDirectionsRendererRef.current.setMap(null);
        taskDetailsDirectionsRendererRef.current = null;
      }
      if (taskDetailsMapInstanceRef.current) {
        taskDetailsMapInstanceRef.current = null;
      }
      setRouteInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTaskDetailsModal, selectedTaskForDetails, farms, tasks]);

  // Funkcja do geokodowania adresu używając Places Autocomplete Service (zamiast Geocoder API)
  const geocodeAddress = useCallback(async (address) => {
    return new Promise(async (resolve, reject) => {
      if (!window.google || !window.google.maps) {
        reject(new Error('Google Maps API nie jest dostępne'));
        return;
      }

      try {
        // Załaduj Places Library jeśli nie jest dostępne
        let placesLibrary = window.google.maps.places;
        if (!placesLibrary && window.google.maps.importLibrary) {
          console.log('📚 [GEOCODE] Ładuję Places Library...');
          placesLibrary = await window.google.maps.importLibrary('places');
        }

        if (!placesLibrary || !placesLibrary.AutocompleteService) {
          reject(new Error('Places Autocomplete Service nie jest dostępne'));
          return;
        }

        const autocompleteService = new placesLibrary.AutocompleteService();
        
        // Wyszukaj miejsce używając Autocomplete Service
        autocompleteService.getPlacePredictions(
          {
            input: address,
            componentRestrictions: { country: 'pl' },
            types: ['geocode']
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
              // Użyj pierwszego wyniku i pobierz szczegóły miejsca
              const placeId = predictions[0].place_id;
              console.log('🔍 [GEOCODE] Znaleziono miejsce:', predictions[0].description, 'place_id:', placeId);
              
              // Pobierz szczegóły miejsca używając PlacesService
              // Użyj istniejącej mapy z modala jeśli jest dostępna, w przeciwnym razie stwórz tymczasową
              let mapElement = null;
              if (routeModalMapRef.current?.mapInstance) {
                mapElement = routeModalMapRef.current.mapInstance;
              } else {
                // Stwórz tymczasową mapę dla PlacesService
                mapElement = document.createElement('div');
                mapElement.style.display = 'none';
                document.body.appendChild(mapElement);
                const tempMap = new window.google.maps.Map(mapElement, {
                  center: { lat: 52.2297, lng: 21.0122 }, // Warszawa jako domyślne centrum
                  zoom: 10
                });
                mapElement = tempMap;
              }
              
              const placesService = new placesLibrary.PlacesService(mapElement);
              placesService.getDetails(
                {
                  placeId: placeId,
                  fields: ['geometry', 'formatted_address', 'name', 'place_id']
                },
                (place, detailsStatus) => {
                  // Usuń tymczasowy element jeśli został utworzony
                  if (mapElement && mapElement.parentNode && mapElement.parentNode === document.body) {
                    document.body.removeChild(mapElement);
                  }
                  
                  if (detailsStatus === window.google.maps.places.PlacesServiceStatus.OK && place && place.geometry) {
                    console.log('✅ [GEOCODE] Geokodowanie zakończone sukcesem:', place);
                    resolve(place);
                  } else {
                    console.error('❌ [GEOCODE] Błąd pobierania szczegółów miejsca:', detailsStatus);
                    reject(new Error(`Nie udało się pobrać szczegółów miejsca: ${detailsStatus}`));
                  }
                }
              );
            } else {
              console.error('❌ [GEOCODE] Błąd wyszukiwania miejsca:', status);
              reject(new Error(`Nie znaleziono miejsca dla "${address}". Status: ${status}`));
            }
          }
        );
      } catch (error) {
        console.error('❌ [GEOCODE] Błąd podczas geokodowania:', error);
        reject(error);
      }
    });
  }, []);

  // Funkcja do geokodowania adresu i wyznaczenia trasy
  const handleGeocodeAndRoute = useCallback(async () => {
    if (!manualOrigin || !manualOrigin.trim()) {
      console.log('❌ [HANDLE GEOCODE] Brak adresu do geokodowania');
      return;
    }

    if (!window.google || !window.google.maps) {
      console.error('❌ [HANDLE GEOCODE] Google Maps API nie jest dostępne');
      setGoogleMapsError('Google Maps API nie jest dostępne. Odśwież stronę.');
      return;
    }

    console.log('🚀 [HANDLE GEOCODE] Rozpoczynam geokodowanie:', manualOrigin);
    setGoogleMapsError(null);
    
    try {
      const place = await geocodeAddress(manualOrigin);
      console.log('✅ [HANDLE GEOCODE] Geokodowanie zakończone sukcesem:', place);
      
      if (place && place.geometry && place.geometry.location) {
        console.log('✅ [HANDLE GEOCODE] Ustawiam manualOriginPlace');
        setManualOriginPlace(place);
        // Aktualizuj tekst w polu input na sformatowany adres
        if (place.formatted_address) {
          setManualOrigin(place.formatted_address);
        }
        
        // Wyznacz trasę po geokodowaniu
        setTimeout(() => {
          console.log('🔄 [HANDLE GEOCODE] Resetuję flagę inicjalizacji i wywołuję initializeRouteModalMap');
          setRouteModalRouteInfo(null);
          isInitializingRouteRef.current = false;
          if (initializeRouteModalMapRef.current) {
            initializeRouteModalMapRef.current();
          }
        }, 300);
      } else {
        console.error('❌ [HANDLE GEOCODE] Place nie ma geometry.location:', place);
        setGoogleMapsError('Nie udało się znaleźć współrzędnych dla podanego adresu.');
      }
    } catch (error) {
      console.error('❌ [HANDLE GEOCODE] Błąd geokodowania:', error);
      setGoogleMapsError(`Błąd geokodowania: ${error.message}. Sprawdź czy adres jest poprawny.`);
    }
  }, [manualOrigin, geocodeAddress]);

  // Inicjalizacja mapy trasy dla modala wyznaczania trasy
  const initializeRouteModalMap = useCallback(() => {
    // Zapobiegaj wielokrotnemu wywoływaniu podczas wpisywania tekstu
    if (isInitializingRouteRef.current) {
      console.log('⏸️ [ROUTE MODAL] initializeRouteModalMap już trwa, pomijam...');
      return;
    }
    
    console.log('🚀 [ROUTE MODAL] initializeRouteModalMap wywołane');
    console.log('🚀 [ROUTE MODAL] window.google:', !!window.google);
    console.log('🚀 [ROUTE MODAL] window.google.maps:', !!window.google?.maps);
    console.log('🚀 [ROUTE MODAL] routeModalMapRef.current:', !!routeModalMapRef.current);
    console.log('🚀 [ROUTE MODAL] selectedDateForRoute:', selectedDateForRoute);
    
    if (!window.google || !window.google.maps || !window.google.maps.Map) {
      console.log('⏳ [ROUTE MODAL] Oczekiwanie na pełne załadowanie Google Maps API...');
      setTimeout(initializeRouteModalMap, 50);
      return;
    }

    if (!routeModalMapRef.current || !selectedDateForRoute) {
      console.log('❌ [ROUTE MODAL] Ref mapy lub data nie jest dostępna');
      console.log('❌ [ROUTE MODAL] routeModalMapRef.current:', routeModalMapRef.current);
      console.log('❌ [ROUTE MODAL] selectedDateForRoute:', selectedDateForRoute);
      return;
    }

    // Ustaw flagę że inicjalizacja trwa
    isInitializingRouteRef.current = true;

    // Wyczyść stare markery i trasę
    if (routeModalMarkersRef.current.length > 0) {
      console.log('🧹 [ROUTE MODAL] Czyszczenie starych markerów:', routeModalMarkersRef.current.length);
      routeModalMarkersRef.current.forEach(marker => marker.setMap(null));
      routeModalMarkersRef.current = [];
    }
    if (routeModalDirectionsRendererRef.current) {
      console.log('🧹 [ROUTE MODAL] Czyszczenie starej trasy');
      routeModalDirectionsRendererRef.current.setMap(null);
      routeModalDirectionsRendererRef.current = null;
    }
    setRouteModalRouteInfo(null);

    // Sprawdź czy używać ręcznie wprowadzonego punktu początkowego czy wszystkie punkty z zadań
    let farmLocations = [];
    
    // Znajdź wszystkie zadania dla wybranego dnia
    console.log('📋 [ROUTE MODAL] Szukanie zadań dla dnia:', selectedDateForRoute);
    console.log('📋 [ROUTE MODAL] Wszystkie zadania:', tasks.length);
    console.log('📋 [ROUTE MODAL] Wszystkie farmy:', farms.length);
    
    const dayTasks = tasks.filter(task => {
      const taskDate = new Date(task.date);
      const normalizedDate = new Date(selectedDateForRoute);
      normalizedDate.setHours(0, 0, 0, 0);
      const taskDateStr = formatDateLocal(taskDate);
      const selectedDateStr = formatDateLocal(normalizedDate);
      return taskDateStr === selectedDateStr;
    });
    
    console.log('📋 [ROUTE MODAL] Znalezione zadania dla dnia:', dayTasks.length);
    console.log('📋 [ROUTE MODAL] Szczegóły zadań:', dayTasks.map(t => ({ 
      farmName: t.farmName, 
      date: t.date, 
      teamName: t.teamName 
    })));
    
    // Znajdź farmy dla wszystkich zadań tego dnia (zawsze pobieramy farmy z zadań jeśli są)
    dayTasks.forEach(task => {
      const farm = farms.find(f => f.farmName === task.farmName);
      console.log(`🔍 [ROUTE MODAL] Szukanie farmy "${task.farmName}":`, farm ? 'ZNALEZIONA' : 'NIE ZNALEZIONA');
      if (farm) {
        console.log(`📍 [ROUTE MODAL] Farm "${task.farmName}" - location:`, farm.location);
      }
      if (farm && farm.location) {
        farmLocations.push({
          farm: farm,
          task: task
        });
      }
    });

    console.log('📍 [ROUTE MODAL] Farmy z lokalizacjami z zadań:', farmLocations.length);
    console.log('📍 [ROUTE MODAL] Szczegóły farm z lokalizacjami:', farmLocations.map(item => ({
      name: item.farm.farmName,
      lat: item.farm.location.latitude,
      lng: item.farm.location.longitude
    })));

    // Jeśli jest ręcznie wprowadzony punkt początkowy, dodaj go na początku (PRZED farmami z zadań)
    console.log('🔍 [ROUTE MODAL] Sprawdzanie ręcznego punktu początkowego:', {
      useManualOrigin,
      hasManualOriginPlace: !!manualOriginPlace,
      manualOrigin,
      manualOriginPlace: manualOriginPlace ? {
        hasGeometry: !!manualOriginPlace.geometry,
        formatted_address: manualOriginPlace.formatted_address,
        name: manualOriginPlace.name
      } : null
    });
    
    if (useManualOrigin && manualOriginPlace && manualOriginPlace.geometry) {
      console.log('✏️ [ROUTE MODAL] Używam ręcznie wprowadzonego punktu początkowego');
      const originLocation = manualOriginPlace.geometry.location;
      
      if (originLocation) {
        const originLat = typeof originLocation.lat === 'function' ? originLocation.lat() : originLocation.lat;
        const originLng = typeof originLocation.lng === 'function' ? originLocation.lng() : originLocation.lng;
        
        farmLocations.unshift({
          farm: {
            farmName: manualOrigin || 'Punkt początkowy',
            location: {
              latitude: originLat,
              longitude: originLng
            }
          },
          task: { teamName: 'Ręcznie wprowadzone', date: selectedDateForRoute }
        });
        console.log('✅ [ROUTE MODAL] Dodano ręcznie wprowadzony punkt początkowy:', {
          name: manualOrigin || 'Punkt początkowy',
          lat: originLat,
          lng: originLng,
          totalLocations: farmLocations.length
        });
      } else {
        console.error('❌ [ROUTE MODAL] manualOriginPlace.geometry.location jest null lub undefined');
      }
    } else {
      console.log('⚠️ [ROUTE MODAL] Nie dodano ręcznego punktu początkowego:', {
        useManualOrigin,
        hasManualOriginPlace: !!manualOriginPlace,
        hasGeometry: manualOriginPlace?.geometry ? true : false
      });
    }

    // Sprawdź czy mamy przynajmniej 2 punkty (minimalnie ręczny punkt + jedna farma z zadań)
    if (farmLocations.length === 0) {
      console.log('❌ [ROUTE MODAL] Brak farm z lokalizacjami dla zadań tego dnia i brak ręcznego punktu początkowego');
      return;
    }
    
    if (farmLocations.length === 1 && !useManualOrigin) {
      console.log('❌ [ROUTE MODAL] Jest tylko jedna farma bez ręcznego punktu początkowego - trasa wymaga minimum 2 punktów');
      return;
    }

    // Sortuj punkty według odległości od punktu początkowego
    if (farmLocations.length > 1) {
      console.log('🔄 [ROUTE MODAL] Sortowanie punktów według odległości od punktu początkowego');
      
      const startPoint = farmLocations[0]; // Punkt początkowy zawsze na pierwszej pozycji
      const otherPoints = farmLocations.slice(1); // Pozostałe punkty
      
      // Funkcja do obliczania odległości (Haversine formula)
      const calculateDistance = (lat1, lng1, lat2, lng2) => {
        const R = 6371; // Promień Ziemi w km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = 
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
          Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Odległość w km
      };
      
      const startLat = startPoint.farm.location.latitude;
      const startLng = startPoint.farm.location.longitude;
      
      // Sortuj pozostałe punkty według odległości od punktu początkowego
      otherPoints.sort((a, b) => {
        const distA = calculateDistance(
          startLat, 
          startLng, 
          a.farm.location.latitude, 
          a.farm.location.longitude
        );
        const distB = calculateDistance(
          startLat, 
          startLng, 
          b.farm.location.latitude, 
          b.farm.location.longitude
        );
        
        return distA - distB; // Sortuj od najbliższego do najdalszego
      });
      
      // Rekonstruuj tablicę z punktem początkowym na początku i posortowanymi punktami
      farmLocations = [startPoint, ...otherPoints];
      
      console.log('✅ [ROUTE MODAL] Punkty posortowane:', farmLocations.map((item, idx) => ({
        index: idx + 1,
        name: item.farm.farmName,
        distance: idx === 0 ? 'START' : `${calculateDistance(startLat, startLng, item.farm.location.latitude, item.farm.location.longitude).toFixed(2)} km`
      })));
    }

    try {
      console.log('🗺️ [ROUTE MODAL] Tworzenie mapy...');
      // Utwórz mapę tylko jeśli jeszcze nie istnieje
      let map;
      if (!routeModalMapRef.current.mapInstance) {
        console.log('🗺️ [ROUTE MODAL] Tworzenie nowej instancji mapy');
        const center = { lat: farmLocations[0].farm.location.latitude, lng: farmLocations[0].farm.location.longitude };
        console.log('🗺️ [ROUTE MODAL] Środek mapy:', center);
        map = new window.google.maps.Map(routeModalMapRef.current, {
          zoom: 10,
          center: center,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP
        });
        routeModalMapRef.current.mapInstance = map;
        console.log('✅ [ROUTE MODAL] Mapa utworzona pomyślnie');
      } else {
        console.log('🔄 [ROUTE MODAL] Używanie istniejącej instancji mapy');
        map = routeModalMapRef.current.mapInstance;
        const center = { lat: farmLocations[0].farm.location.latitude, lng: farmLocations[0].farm.location.longitude };
        map.setCenter(center);
        map.setZoom(10);
        console.log('✅ [ROUTE MODAL] Mapa zaktualizowana');
      }

      // Dodaj markery dla wszystkich farm
      console.log('📍 [ROUTE MODAL] Dodawanie markerów:', farmLocations.length);
      farmLocations.forEach((item, index) => {
        const position = { lat: item.farm.location.latitude, lng: item.farm.location.longitude };
        console.log(`📍 [ROUTE MODAL] Dodawanie markera ${index + 1}/${farmLocations.length}:`, {
          name: item.farm.farmName,
          position: position
        });
        
        const marker = new window.google.maps.Marker({
          position: position,
          map: map,
          title: item.farm.farmName,
          icon: {
            url: index === 0 
              ? 'http://maps.google.com/mapfiles/ms/icons/green-dot.png'
              : index === farmLocations.length - 1
              ? 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
              : 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'
          },
          label: {
            text: String(index + 1),
            color: 'white',
            fontWeight: 'bold'
          }
        });

        console.log(`✅ [ROUTE MODAL] Marker ${index + 1} dodany:`, marker);

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div>
              <strong>${item.farm.farmName}</strong><br/>
              Ekipa: ${item.task.teamName}<br/>
              Godzina: ${new Date(item.task.date).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

        routeModalMarkersRef.current.push(marker);
      });
      
      console.log(`✅ [ROUTE MODAL] Wszystkie markery dodane (${routeModalMarkersRef.current.length})`);

      // Jeśli są co najmniej 2 farmy, narysuj trasę
      if (farmLocations.length >= 2 && window.google.maps.DirectionsService) {
        console.log('🛣️ [ROUTE MODAL] Rozpoczynam obliczanie trasy dla', farmLocations.length, 'farm');
        const directionsService = new window.google.maps.DirectionsService();
        const directionsRenderer = new window.google.maps.DirectionsRenderer({
          map: map,
          suppressMarkers: true
        });

        routeModalDirectionsRendererRef.current = directionsRenderer;

        // Przygotuj waypoints (wszystkie farmy oprócz pierwszej i ostatniej)
        const waypoints = farmLocations.slice(1, -1).map(item => ({
          location: { lat: item.farm.location.latitude, lng: item.farm.location.longitude },
          stopover: true
        }));

        const origin = { lat: farmLocations[0].farm.location.latitude, lng: farmLocations[0].farm.location.longitude };
        const destination = { lat: farmLocations[farmLocations.length - 1].farm.location.latitude, lng: farmLocations[farmLocations.length - 1].farm.location.longitude };

        const request = {
          origin: origin,
          destination: destination,
          waypoints: waypoints,
          optimizeWaypoints: true,
          travelMode: window.google.maps.TravelMode.DRIVING
        };

        console.log('🛣️ [ROUTE MODAL] Request do DirectionsService:', {
          origin: origin,
          destination: destination,
          waypoints: waypoints.length,
          optimizeWaypoints: true
        });

        console.log('🛣️ [ROUTE MODAL] Wywołuję directionsService.route...');
        directionsService.route(request, (result, status) => {
          console.log('🛣️ [ROUTE MODAL] Otrzymano odpowiedź z DirectionsService:', {
            status: status,
            statusText: status === window.google.maps.DirectionsStatus.OK ? 'OK' : status,
            hasResult: !!result
          });
          
          if (status === window.google.maps.DirectionsStatus.OK) {
            console.log('✅ [ROUTE MODAL] Trasa obliczona pomyślnie');
            console.log('🛣️ [ROUTE MODAL] Liczba tras:', result.routes?.length);
            console.log('🛣️ [ROUTE MODAL] Liczba odcinków:', result.routes?.[0]?.legs?.length);
            
            directionsRenderer.setDirections(result);
            console.log('✅ [ROUTE MODAL] Trasa wyświetlona na mapie');
            
            // Zapisz informacje o trasie
            let totalDistance = 0;
            let totalDuration = 0;
            
            result.routes[0].legs.forEach(leg => {
              totalDistance += leg.distance.value; // w metrach
              totalDuration += leg.duration.value; // w sekundach
            });
            
            const distanceKm = (totalDistance / 1000).toFixed(1);
            const durationHours = Math.floor(totalDuration / 3600);
            const durationMinutes = Math.floor((totalDuration % 3600) / 60);
            
            console.log('📊 [ROUTE MODAL] Informacje o trasie:', {
              distance: distanceKm + ' km',
              duration: durationHours > 0 
                ? `${durationHours}h ${durationMinutes}min`
                : `${durationMinutes}min`
            });
            
            setRouteModalRouteInfo({
              distance: distanceKm,
              duration: durationHours > 0 
                ? `${durationHours}h ${durationMinutes}min`
                : `${durationMinutes}min`,
              durationSeconds: totalDuration,
              routeData: result // Zapisz pełne dane trasy dla udostępnienia
            });
          } else {
            console.error('❌ [ROUTE MODAL] Błąd podczas pobierania trasy:', status);
            console.error('❌ [ROUTE MODAL] Szczegóły błędu:', {
              status: status,
              statusText: status === window.google.maps.DirectionsStatus.ZERO_RESULTS ? 'ZERO_RESULTS' :
                          status === window.google.maps.DirectionsStatus.NOT_FOUND ? 'NOT_FOUND' :
                          status === window.google.maps.DirectionsStatus.REQUEST_DENIED ? 'REQUEST_DENIED' :
                          status === window.google.maps.DirectionsStatus.OVER_QUERY_LIMIT ? 'OVER_QUERY_LIMIT' :
                          status === window.google.maps.DirectionsStatus.INVALID_REQUEST ? 'INVALID_REQUEST' :
                          status === window.google.maps.DirectionsStatus.UNKNOWN_ERROR ? 'UNKNOWN_ERROR' : 'UNKNOWN'
            });
            
            let errorMessage = 'Błąd podczas pobierania trasy. ';
            if (status === window.google.maps.DirectionsStatus.REQUEST_DENIED) {
              errorMessage = 'Directions API zwraca REQUEST_DENIED mimo że jest włączone. ';
              errorMessage += 'Możliwe przyczyny: ';
              errorMessage += '1. Ograniczenia aplikacji (Application restrictions) w kluczu API - sprawdź czy domena "farm.my-it.pl" jest dodana do "Witryny", ';
              errorMessage += '2. Billing account może nie być włączony - Directions API wymaga aktywnego billing account, ';
              errorMessage += '3. Poczekaj kilka minut po włączeniu API - może potrzebować czasu na aktywację, ';
              errorMessage += '4. Sprawdź czy klucz API ma dostęp do Directions API w ograniczeniach API (powinien być na liście).';
              setGoogleMapsError(errorMessage);
            }
            
            setRouteModalRouteInfo(null);
          }
        });
      } else {
        if (farmLocations.length < 2) {
          console.log('⚠️ [ROUTE MODAL] Za mało farm do wyznaczenia trasy (minimum 2, mamy:', farmLocations.length, ')');
        }
        if (!window.google.maps.DirectionsService) {
          console.error('❌ [ROUTE MODAL] DirectionsService nie jest dostępne');
        }
      }

      // Dostosuj zoom aby pokazać wszystkie farmy
      if (farmLocations.length > 1 && window.google.maps.LatLngBounds) {
        console.log('🔍 [ROUTE MODAL] Dostosowuję zoom do wszystkich farm');
        const bounds = new window.google.maps.LatLngBounds();
        farmLocations.forEach(item => {
          bounds.extend({ lat: item.farm.location.latitude, lng: item.farm.location.longitude });
        });
        map.fitBounds(bounds);
        console.log('✅ [ROUTE MODAL] Zoom dostosowany');
      }
      
      // Zresetuj flagę po zakończeniu inicjalizacji
      isInitializingRouteRef.current = false;
      console.log('✅ [ROUTE MODAL] Inicjalizacja zakończona');
    } catch (error) {
      console.error('❌ [ROUTE MODAL] Błąd inicjalizacji mapy trasy:', error);
      console.error('❌ [ROUTE MODAL] Stack trace:', error.stack);
      // Zresetuj flagę także w przypadku błędu
      isInitializingRouteRef.current = false;
    }
  }, [selectedDateForRoute, farms, tasks, useManualOrigin, manualOriginPlace]); // Usunięto manualOrigin z zależności aby uniknąć resetowania podczas wpisywania

  // Zapisz funkcję initializeRouteModalMap w refie
  useEffect(() => {
    initializeRouteModalMapRef.current = initializeRouteModalMap;
  }, [initializeRouteModalMap]);

  // Załaduj Google Maps API i zainicjalizuj mapę trasy
  useEffect(() => {
    console.log('🎯 [ROUTE MODAL EFFECT] useEffect wywołany:', {
      showRouteModal,
      selectedDateForRoute,
      farmsLength: farms.length,
      useManualOrigin,
      hasManualOrigin: !!manualOriginPlace
    });
    
    const shouldInitialize = showRouteModal && selectedDateForRoute && farms.length > 0 && (
      !useManualOrigin || (useManualOrigin && manualOriginPlace)
    );
    
    if (shouldInitialize) {
      console.log('🎯 [ROUTE MODAL EFFECT] Warunki spełnione, inicjalizuję mapę...');
      setGoogleMapsError(null); // Wyczyść poprzednie błędy
      
      // Funkcja pomocnicza do ładowania skryptu Google Maps
      const loadGoogleMapsScript = () => {
        console.log('📥 [ROUTE MODAL EFFECT] Ładuję skrypt Google Maps API');
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY'}&libraries=places,directions`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          console.log('✅ [ROUTE MODAL EFFECT] Skrypt Google Maps API załadowany');
          script.dataset.loaded = 'true';
          requestAnimationFrame(() => {
            initializeRouteModalMap();
          });
        };
        script.onerror = (error) => {
          console.error('❌ [ROUTE MODAL EFFECT] Błąd ładowania skryptu Google Maps API:', error);
          // Sprawdź czy klucz API jest ustawiony
          const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
          let errorMessage = 'Błąd ładowania Google Maps API. ';
          if (!apiKey || apiKey === 'YOUR_API_KEY') {
            errorMessage += 'Brak klucza API. Ustaw REACT_APP_GOOGLE_MAPS_API_KEY w pliku .env';
            console.error('❌ [ROUTE MODAL EFFECT] Brak klucza Google Maps API! Ustaw REACT_APP_GOOGLE_MAPS_API_KEY w pliku .env');
          } else {
            errorMessage += 'Sprawdź konfigurację klucza API w Google Cloud Console (Maps JavaScript API, Places API, Directions API muszą być włączone).';
            console.error('❌ [ROUTE MODAL EFFECT] Błąd może być spowodowany:');
            console.error('  1. Nieprawidłowym kluczem API');
            console.error('  2. Brakiem włączonych API w Google Cloud Console (Maps JavaScript API, Places API, Directions API)');
            console.error('  3. Ograniczeniami domeny w konfiguracji klucza API');
          }
          setGoogleMapsError(errorMessage);
        };
        document.head.appendChild(script);
      };
      
      const initializeMapWhenReady = () => {
        console.log('🎯 [ROUTE MODAL EFFECT] initializeMapWhenReady wywołane');
        console.log('🎯 [ROUTE MODAL EFFECT] routeModalMapRef.current:', !!routeModalMapRef.current);
        
        if (!routeModalMapRef.current) {
          console.log('⏳ [ROUTE MODAL EFFECT] Oczekiwanie na routeModalMapRef.current...');
          setTimeout(initializeMapWhenReady, 10);
          return;
        }

        // Jeśli Google Maps API jest już załadowane, od razu inicjalizuj
        if (window.google && window.google.maps && window.google.maps.Map) {
          // Sprawdź czy potrzebne biblioteki są dostępne
          const hasPlaces = window.google.maps.places && window.google.maps.places.Autocomplete;
          const hasDirections = window.google.maps.DirectionsService && window.google.maps.DirectionsRenderer;
          
          if (hasPlaces && hasDirections) {
            console.log('✅ [ROUTE MODAL EFFECT] Google Maps API już załadowane z wszystkimi bibliotekami');
            requestAnimationFrame(() => {
              console.log('🎬 [ROUTE MODAL EFFECT] requestAnimationFrame callback - wywołuję initializeRouteModalMap');
              initializeRouteModalMap();
            });
            return;
          } else {
            console.log('⚠️ [ROUTE MODAL EFFECT] Google Maps API załadowane, ale brakuje bibliotek:', {
              hasPlaces,
              hasDirections
            });
            // Spróbuj użyć dynamicznego importu biblioteki directions jeśli jest dostępne
            if (window.google && window.google.maps && window.google.maps.importLibrary) {
              console.log('📚 [ROUTE MODAL EFFECT] Używam importLibrary do załadowania biblioteki directions');
              window.google.maps.importLibrary('directions').then(() => {
                console.log('✅ [ROUTE MODAL EFFECT] Biblioteka directions załadowana');
                requestAnimationFrame(() => {
                  initializeRouteModalMap();
                });
              }).catch((error) => {
                console.error('❌ [ROUTE MODAL EFFECT] Błąd ładowania biblioteki directions:', error);
                // Fallback - załaduj skrypt ponownie
                console.log('📥 [ROUTE MODAL EFFECT] Fallback - ładuję skrypt ponownie z bibliotekami directions');
                loadGoogleMapsScript();
              });
            } else {
              // Jeśli brakuje bibliotek, załaduj skrypt ponownie z wszystkimi bibliotekami
              console.log('📥 [ROUTE MODAL EFFECT] importLibrary niedostępne - ładuję skrypt ponownie z bibliotekami directions');
              loadGoogleMapsScript();
            }
            return;
          }
        }

        // Sprawdź czy skrypt jest już w trakcie ładowania
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          console.log('📜 [ROUTE MODAL EFFECT] Znaleziono istniejący skrypt Google Maps');
          // Jeśli API jest już dostępne, sprawdź czy wszystkie biblioteki są dostępne
          if (window.google && window.google.maps && window.google.maps.Map) {
            const hasPlaces = window.google.maps.places && window.google.maps.places.Autocomplete;
            const hasDirections = window.google.maps.DirectionsService && window.google.maps.DirectionsRenderer;
            
            if (hasPlaces && hasDirections) {
              console.log('✅ [ROUTE MODAL EFFECT] Google Maps API już załadowane z wszystkimi bibliotekami (skrypt istnieje)');
              existingScript.dataset.loaded = 'true';
              requestAnimationFrame(() => {
                initializeRouteModalMap();
              });
              return;
            } else {
              console.log('⚠️ [ROUTE MODAL EFFECT] Brakuje bibliotek, ładuję ponownie:', { hasPlaces, hasDirections });
            }
          }
          // Jeśli skrypt jest oznaczony jako załadowany, ale API jeszcze nie jest dostępne, poczekaj chwilę
          if (existingScript.dataset.loaded === 'true') {
            console.log('⏳ [ROUTE MODAL EFFECT] Skrypt oznaczony jako załadowany, czekam na API...');
            setTimeout(() => {
              if (window.google && window.google.maps && window.google.maps.Map) {
                const hasPlaces = window.google.maps.places && window.google.maps.places.Autocomplete;
                const hasDirections = window.google.maps.DirectionsService && window.google.maps.DirectionsRenderer;
                if (hasPlaces && hasDirections) {
                  initializeRouteModalMap();
                } else {
                  console.error('❌ [ROUTE MODAL EFFECT] Brakuje bibliotek pomimo załadowanego skryptu');
                  // Załaduj ponownie z wszystkimi bibliotekami
                  loadGoogleMapsScript();
                }
              } else {
                console.error('❌ [ROUTE MODAL EFFECT] API nie jest dostępne pomimo załadowanego skryptu');
                setGoogleMapsError('Google Maps API nie jest dostępne. Spróbuj odświeżyć stronę.');
              }
            }, 500);
            return;
          }
          // Skrypt jest w trakcie ładowania, dodaj listener
          console.log('⏳ [ROUTE MODAL EFFECT] Skrypt w trakcie ładowania, dodaję listener');
          existingScript.addEventListener('load', () => {
            console.log('✅ [ROUTE MODAL EFFECT] Skrypt załadowany (z listenera)');
            existingScript.dataset.loaded = 'true';
            requestAnimationFrame(() => {
              initializeRouteModalMap();
            });
          }, { once: true });
          return;
        }

        // Załaduj skrypt Google Maps API
        loadGoogleMapsScript();
      };

      // Krótkie opóźnienie aby upewnić się że DOM jest gotowy
      requestAnimationFrame(() => {
        initializeMapWhenReady();
      });
    } else {
      console.log('🧹 [ROUTE MODAL EFFECT] Czyszczenie - modal zamknięty lub brak danych');
      // Wyczyść mapę gdy modal się zamyka
      if (routeModalMarkersRef.current.length > 0) {
        console.log('🧹 [ROUTE MODAL EFFECT] Usuwam markery:', routeModalMarkersRef.current.length);
        routeModalMarkersRef.current.forEach(marker => marker.setMap(null));
        routeModalMarkersRef.current = [];
      }
      if (routeModalDirectionsRendererRef.current) {
        console.log('🧹 [ROUTE MODAL EFFECT] Usuwam renderer trasy');
        routeModalDirectionsRendererRef.current.setMap(null);
        routeModalDirectionsRendererRef.current = null;
      }
      // Wyczyść instancję mapy
      if (routeModalMapRef.current?.mapInstance) {
        console.log('🧹 [ROUTE MODAL EFFECT] Usuwam instancję mapy');
        routeModalMapRef.current.mapInstance = null;
      }
      setRouteModalRouteInfo(null);
    }
  }, [showRouteModal, selectedDateForRoute, farms, initializeRouteModalMap, useManualOrigin, manualOriginPlace]);

  // Inicjalizacja autocomplete dla ręcznie wprowadzonego punktu początkowego
  useEffect(() => {
    if (!showRouteModal || !useManualOrigin) {
      // Wyczyść autocomplete jeśli checkbox jest odznaczony
      if (originAutocompleteInstanceRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(originAutocompleteInstanceRef.current);
        originAutocompleteInstanceRef.current = null;
      }
      return;
    }

    // Poczekaj aż Google Maps API będzie dostępne i załaduj Places Library
    const initializeAutocomplete = () => {
      const checkAndInit = async () => {
        if (!window.google || !window.google.maps) {
          console.log('⏳ [AUTOCOMPLETE] Oczekiwanie na Google Maps API...');
          setTimeout(checkAndInit, 100);
          return;
        }

        // Sprawdź czy ref jest dostępny
        if (!originAutocompleteRef.current) {
          console.log('⏳ [AUTOCOMPLETE] Oczekiwanie na originAutocompleteRef.current...');
          setTimeout(checkAndInit, 100);
          return;
        }

        // Załaduj Places Library używając nowego API
        try {
          let placesLibrary = window.google.maps.places;
          
          // Jeśli nie ma Places API, spróbuj załadować przez importLibrary
          if (!placesLibrary && window.google.maps.importLibrary) {
            console.log('📚 [AUTOCOMPLETE] Ładuję Places Library przez importLibrary...');
            placesLibrary = await window.google.maps.importLibrary('places');
          }
          
          if (!placesLibrary || !placesLibrary.Autocomplete) {
            console.error('❌ [AUTOCOMPLETE] Places Autocomplete nie jest dostępne');
            setGoogleMapsError('Places API nie jest dostępne. Upewnij się że Places API (New) jest włączone w Google Cloud Console.');
            return;
          }

          // Dodatkowe opóźnienie aby upewnić się że wszystko jest gotowe
          setTimeout(() => {
            // Inicjalizuj autocomplete dla punktu początkowego
            if (originAutocompleteRef.current && !originAutocompleteInstanceRef.current) {
              console.log('🔍 [AUTOCOMPLETE] Inicjalizuję autocomplete dla punktu początkowego');
              console.log('🔍 [AUTOCOMPLETE] originAutocompleteRef.current:', originAutocompleteRef.current);
              console.log('🔍 [AUTOCOMPLETE] Places Library:', placesLibrary);
              
              try {
                // Sprawdź czy mapa jest już dostępna (dla lepszej integracji)
                const map = routeModalMapRef.current?.mapInstance;
                
                const autocompleteOptions = {
                  types: ['geocode'], // Używamy tylko geocode aby zmniejszyć liczbę sugestii
                  componentRestrictions: { country: 'pl' },
                  fields: ['geometry', 'formatted_address', 'name', 'place_id']
                };
                
                // Dodaj mapę jeśli jest dostępna (opcjonalne, ale może pomóc)
                if (map) {
                  try {
                    const bounds = map.getBounds();
                    if (bounds) {
                      autocompleteOptions.bounds = bounds;
                    }
                  } catch (e) {
                    console.log('⚠️ [AUTOCOMPLETE] Nie można pobrać bounds z mapy:', e);
                  }
                }
                
                console.log('🔍 [AUTOCOMPLETE] Opcje autocomplete:', autocompleteOptions);
                
                originAutocompleteInstanceRef.current = new placesLibrary.Autocomplete(
                  originAutocompleteRef.current,
                  autocompleteOptions
                );

                originAutocompleteInstanceRef.current.addListener('place_changed', () => {
                  const place = originAutocompleteInstanceRef.current.getPlace();
                  console.log('📍 [AUTOCOMPLETE] Wybrano punkt początkowy:', place);
                  if (place.geometry) {
                    setManualOriginPlace(place);
                    setManualOrigin(place.formatted_address || place.name);
                    // Automatycznie wyznacz trasę po wybraniu punktu
                    setTimeout(() => {
                      setRouteModalRouteInfo(null);
                      initializeRouteModalMap();
                    }, 100);
                  } else {
                    console.error('❌ [AUTOCOMPLETE] Wybrane miejsce nie ma współrzędnych geometry:', place);
                  }
                });
                console.log('✅ [AUTOCOMPLETE] Autocomplete zainicjalizowane pomyślnie');
              } catch (error) {
                console.error('❌ [AUTOCOMPLETE] Błąd inicjalizacji autocomplete:', error);
                console.error('❌ [AUTOCOMPLETE] Stack trace:', error.stack);
                setGoogleMapsError(`Błąd inicjalizacji autocomplete: ${error.message}. Sprawdź czy Places API (New) jest włączone w Google Cloud Console.`);
              }
            }
          }, 200); // Opóźnienie 200ms aby upewnić się że wszystko jest gotowe
        } catch (error) {
          console.error('❌ [AUTOCOMPLETE] Błąd ładowania Places Library:', error);
          setGoogleMapsError(`Błąd ładowania Places API: ${error.message}. Sprawdź czy Places API (New) jest włączone.`);
        }
      };
      
      checkAndInit();
    };

    initializeAutocomplete();

    return () => {
      // Cleanup przy odznaczeniu checkboxa lub zamknięciu modala
      if (originAutocompleteInstanceRef.current) {
        try {
          window.google?.maps?.event?.clearInstanceListeners(originAutocompleteInstanceRef.current);
        } catch (e) {
          console.error('Błąd podczas czyszczenia autocomplete:', e);
        }
        originAutocompleteInstanceRef.current = null;
      }
    };
  }, [showRouteModal, useManualOrigin, initializeRouteModalMap]);

  // Geokodowanie adresu gdy użytkownik wpisze tekst ręcznie (nie wybierze z autocomplete)
  useEffect(() => {
    console.log('🎯 [GEOCODE EFFECT] useEffect wywołany:', {
      showRouteModal,
      useManualOrigin,
      manualOrigin,
      hasManualOriginPlace: !!manualOriginPlace
    });

    if (!showRouteModal || !useManualOrigin || !manualOrigin) {
      // Nie geokoduj jeśli:
      // - modal nie jest otwarty
      // - checkbox nie jest zaznaczony
      // - nie ma tekstu
      console.log('⏸️ [GEOCODE EFFECT] Warunki nie spełnione, pomijam geokodowanie');
      return;
    }

    // Jeśli jest manualOriginPlace, sprawdź czy tekst się różni
    if (manualOriginPlace) {
      const placeText = manualOriginPlace.formatted_address || manualOriginPlace.name || '';
      const normalizedPlaceText = placeText.toLowerCase().trim();
      const normalizedManualOrigin = manualOrigin.toLowerCase().trim();
      
      console.log('🔍 [GEOCODE EFFECT] Porównywanie tekstów:', {
        placeText: normalizedPlaceText,
        manualOrigin: normalizedManualOrigin,
        match: normalizedPlaceText === normalizedManualOrigin
      });
      
      if (normalizedPlaceText === normalizedManualOrigin) {
        // Tekst jest taki sam jak wybrane miejsce - nie geokoduj ponownie
        console.log('✅ [GEOCODE EFFECT] Tekst się zgadza z wybranym miejscem, pomijam geokodowanie');
        return;
      }
      // Tekst się różni - wyczyść manualOriginPlace aby geokodować nowy tekst
      console.log('🔄 [GEOCODE EFFECT] Tekst się różni, czyszczę manualOriginPlace');
      setManualOriginPlace(null);
      // Nie zwracaj return - pozwól na kontynuację w następnym cyklu useEffect
      return;
    }

    // Czyszczenie poprzedniego timeoutu
    if (manualOriginTimeoutRef.current) {
      clearTimeout(manualOriginTimeoutRef.current);
      manualOriginTimeoutRef.current = null;
    }

    console.log('⏱️ [GEOCODE EFFECT] Ustawiam timeout na geokodowanie (2 sekundy)');
    
    // Geokoduj adres po debounce (2 sekundy po ostatniej zmianie)
    manualOriginTimeoutRef.current = setTimeout(async () => {
      console.log('⏰ [GEOCODE EFFECT] Timeout wywołany, rozpoczynam geokodowanie');
      
      if (!window.google || !window.google.maps) {
        console.log('⏳ [GEOCODE] Oczekiwanie na Google Maps API...');
        return;
      }

      console.log('🔍 [GEOCODE] Geokodowanie adresu:', manualOrigin);
      try {
        const place = await geocodeAddress(manualOrigin);
        console.log('✅ [GEOCODE] Otrzymano wynik geokodowania:', place);
        
        if (place && place.geometry && place.geometry.location) {
          console.log('✅ [GEOCODE] Ustawiam manualOriginPlace:', {
            formatted_address: place.formatted_address,
            name: place.name,
            hasLocation: !!place.geometry.location
          });
          setManualOriginPlace(place);
          // Wyznacz trasę automatycznie po geokodowaniu
          setTimeout(() => {
            console.log('🔄 [GEOCODE] Resetuję flagę inicjalizacji i wywołuję initializeRouteModalMap');
            setRouteModalRouteInfo(null);
            isInitializingRouteRef.current = false; // Reset flagi aby pozwolić na inicjalizację
            initializeRouteModalMap();
          }, 300);
        } else {
          console.error('❌ [GEOCODE] Place nie ma geometry.location:', place);
        }
      } catch (error) {
        console.error('❌ [GEOCODE] Błąd geokodowania:', error);
        console.error('❌ [GEOCODE] Stack trace:', error.stack);
        // Nie pokazuj błędu użytkownikowi - może jeszcze wybierać z autocomplete
      }
    }, 2000); // 2 sekundy debounce

    return () => {
      console.log('🧹 [GEOCODE EFFECT] Cleanup - czyszczenie timeoutu');
      if (manualOriginTimeoutRef.current) {
        clearTimeout(manualOriginTimeoutRef.current);
        manualOriginTimeoutRef.current = null;
      }
    };
  }, [showRouteModal, useManualOrigin, manualOrigin, manualOriginPlace, geocodeAddress, initializeRouteModalMap]);

  // Funkcja do generowania linku Google Maps z trasą
  const generateGoogleMapsRouteLink = useCallback(() => {
    if (!routeModalRouteInfo?.routeData || !selectedDateForRoute) return null;

    const dayTasks = getTasksForDate(selectedDateForRoute);
    const farmLocations = [];
    
    // Jeśli jest ręcznie wprowadzony punkt początkowy, dodaj go na początku
    if (useManualOrigin && manualOriginPlace) {
      const originLocation = manualOriginPlace.geometry?.location;
      if (originLocation) {
        farmLocations.push({
          lat: originLocation.lat(),
          lng: originLocation.lng()
        });
      }
    }
    
    // Dodaj farmy z zadań
    dayTasks.forEach(task => {
      const farm = farms.find(f => f.farmName === task.farmName);
      if (farm && farm.location) {
        farmLocations.push({
          lat: farm.location.latitude,
          lng: farm.location.longitude,
          name: farm.farmName
        });
      }
    });

    if (farmLocations.length < 2) return null;

    // Utwórz URL dla Google Maps Directions
    const origin = `${farmLocations[0].lat},${farmLocations[0].lng}`;
    const destination = `${farmLocations[farmLocations.length - 1].lat},${farmLocations[farmLocations.length - 1].lng}`;
    const waypoints = farmLocations.slice(1, -1).map(loc => `${loc.lat},${loc.lng}`).join('|');
    
    const googleMapsUrl = waypoints 
      ? `https://www.google.com/maps/dir/${origin}/${waypoints}/${destination}`
      : `https://www.google.com/maps/dir/${origin}/${destination}`;

    return googleMapsUrl;
  }, [selectedDateForRoute, routeModalRouteInfo, farms, tasks, useManualOrigin, manualOriginPlace]);

  // Funkcja do kopiowania linku trasy
  const handleShareRoute = async () => {
    const routeLink = generateGoogleMapsRouteLink();
    if (routeLink) {
      try {
        await navigator.clipboard.writeText(routeLink);
        toast.success('Link do trasy został skopiowany do schowka!');
      } catch (error) {
        // Fallback dla przeglądarek bez Clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = routeLink;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          toast.success('Link do trasy został skopiowany do schowka!');
        } catch (err) {
          toast.error('Nie udało się skopiować linku');
        }
        document.body.removeChild(textArea);
      }
    }
  };

  // Funkcje do otwierania modali
  const openAddTeamModal = (callback) => {
    if (user?.role !== 'super admin' && user?.role !== 'admin' && !user?.moduleAccess?.planner) {
      toast.error('Brak uprawnień do dodawania ekip');
      return;
    }
    resetTeamForm();
    setEditingTeam(null);
    setShowTeamModal(true);
    // Zapisz callback do wykonania po zamknięciu modala
    if (callback) {
      window._plannerTeamCallback = callback;
    }
  };

  const openEditTeamModal = (team) => {
    if (user?.role !== 'super admin' && user?.role !== 'admin' && !user?.moduleAccess?.planner) {
      toast.error('Brak uprawnień do edycji ekip');
      return;
    }
    const firstPersonId = team.firstServicePerson?._id?.toString() || team.firstServicePerson?.toString();
    const secondPersonId = team.secondServicePerson?._id?.toString() || team.secondServicePerson?.toString();
    setTeamFormData({
      teamName: team.teamName,
      firstServicePerson: firstPersonId || '',
      secondServicePerson: secondPersonId || '',
      color: team.color || '#3B82F6'
    });
    setEditingTeam(team);
    setShowTeamModal(true);
  };

  const openAddFarmModal = (callback) => {
    if (user?.role !== 'super admin' && user?.role !== 'admin' && !user?.moduleAccess?.planner) {
      toast.error('Brak uprawnień do dodawania farm');
      return;
    }
    resetFarmForm();
    setEditingFarm(null);
    setShowFarmModal(true);
    // Zapisz callback do wykonania po zamknięciu modala
    if (callback) {
      window._plannerFarmCallback = callback;
    }
  };

  const openEditFarmModal = (farm) => {
    if (user?.role !== 'super admin' && user?.role !== 'admin' && !user?.moduleAccess?.planner) {
      toast.error('Brak uprawnień do edycji farm');
      return;
    }
    setFarmFormData({
      farmName: farm.farmName,
      latitude: farm.location?.latitude?.toString() || '',
      longitude: farm.location?.longitude?.toString() || ''
    });
    setEditingFarm(farm);
    setShowFarmModal(true);
  };

  // Obsługa formularzy
  const handleTeamSubmit = (e) => {
    e.preventDefault();
    if (user?.role !== 'super admin' && user?.role !== 'admin' && !user?.moduleAccess?.planner) {
      toast.error('Brak uprawnień');
      return;
    }

    const teamData = {
      teamName: teamFormData.teamName,
      firstServicePerson: teamFormData.firstServicePerson,
      secondServicePerson: teamFormData.secondServicePerson || null,
      color: teamFormData.color
    };

    if (editingTeam) {
      updateTeamMutation.mutate({ id: editingTeam._id, data: teamData });
    } else {
      addTeamMutation.mutate(teamData);
    }
  };

  const handleFarmSubmit = (e) => {
    e.preventDefault();
    if (user?.role !== 'super admin' && user?.role !== 'admin' && !user?.moduleAccess?.planner) {
      toast.error('Brak uprawnień');
      return;
    }

    const latitude = parseFloat(farmFormData.latitude);
    const longitude = parseFloat(farmFormData.longitude);

    if (isNaN(latitude) || isNaN(longitude)) {
      toast.error('Nieprawidłowe współrzędne GPS');
      return;
    }

    const farmData = {
      farmName: farmFormData.farmName,
      latitude,
      longitude
    };

    if (editingFarm) {
      updateFarmMutation.mutate({ id: editingFarm._id, data: farmData });
    } else {
      addFarmMutation.mutate(farmData);
    }
  };

  const handleDeleteTeam = (teamId) => {
    if (user?.role !== 'super admin' && user?.role !== 'admin' && !user?.moduleAccess?.planner) {
      toast.error('Brak uprawnień do usuwania ekip');
      return;
    }
    if (window.confirm('Czy na pewno chcesz usunąć tę ekipę?')) {
      deleteTeamMutation.mutate(teamId);
    }
  };

  const handleDeleteFarm = (farmId) => {
    if (user?.role !== 'super admin' && user?.role !== 'admin' && !user?.moduleAccess?.planner) {
      toast.error('Brak uprawnień do usuwania farm');
      return;
    }
    if (window.confirm('Czy na pewno chcesz usunąć tę farmę?')) {
      deleteFarmMutation.mutate(farmId);
    }
  };

  // Funkcje drag and drop
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', task._id);
    // Dodaj klasę do elementu przeciąganego
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDragOverDay(null);
  };

  const handleDragOver = (e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(date);
  };

  const handleDragLeave = (e) => {
    // Tylko jeśli opuszczamy komórkę dnia, nie wewnętrzne elementy
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverDay(null);
    }
  };

  const handleDrop = (e, targetDate) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedTask) {
      setDragOverDay(null);
      return;
    }

    const currentTaskDate = new Date(draggedTask.date);
    const currentDateISO = currentTaskDate.toISOString();
    const targetDateISO = targetDate.toISOString();

    // Porównaj pełne daty z godziną - jeśli się nie zmieniły, nie rób nic
    if (currentDateISO === targetDateISO) {
      setDraggedTask(null);
      setDragOverDay(null);
      return;
    }

    // Aktualizuj datę zadania - wyślij pełną datę z godziną
    moveTaskMutation.mutate({
      id: draggedTask._id,
      newDate: targetDateISO
    });
  };

  const resetForm = () => {
    setFormData({
      farmName: '',
      teamName: '',
      date: '',
      time: '06:00',
      kanbanCardIds: []
    });
  };

  const openAddModal = (date) => {
    // Admin i super admin mogą dodawać zadania
    if (user?.role !== 'super admin' && user?.role !== 'admin') {
      toast.error('Brak uprawnień do dodawania zadań');
      return;
    }
    
    // Użyj lokalnej daty, nie UTC - aby uniknąć przesunięcia o dzień
    const dateStr = formatDateLocal(date);
    // Jeśli zadanie ma godzinę, wyciągnij ją; w przeciwnym razie użyj domyślnej 6:00
    const timeStr = date instanceof Date && !isNaN(date.getHours()) && date.getHours() !== 0
      ? `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
      : '06:00';
    setFormData({
      farmName: '',
      teamName: '',
      date: dateStr,
      time: timeStr,
      kanbanCardIds: []
    });
    setEditingTask(null);
    setShowModal(true);
  };

  const openEditModal = (task) => {
    // Admin i super admin mogą edytować zadania
    if (user?.role !== 'super admin' && user?.role !== 'admin') {
      toast.error('Brak uprawnień do edycji zadań');
      return;
    }
    
    const taskDate = new Date(task.date);
    const timeStr = `${String(taskDate.getHours()).padStart(2, '0')}:${String(taskDate.getMinutes()).padStart(2, '0')}`;
    // Użyj lokalnej daty, nie UTC
    const dateStr = formatDateLocal(taskDate);
    
    // Filtruj nieistniejące karty Kanban - usuń stare przypisania
    const validKanbanCardIds = (task.kanbanCardIds || []).filter(cardId => {
      // Sprawdź czy karta istnieje w aktualnym Kanban (z API)
      const existsInApi = kanbanLists.some(list => 
        list.cards.some(card => String(card.id) === String(cardId))
      );
      
      // Jeśli nie istnieje w API, sprawdź localStorage (fallback)
      if (!existsInApi) {
        const cardInfo = findCard(cardId);
        return !!cardInfo;
      }
      
      return true;
    });
    
    // Jeśli były stare karty, usuń je i pokaż ostrzeżenie
    if (validKanbanCardIds.length !== (task.kanbanCardIds || []).length) {
      const removedCount = (task.kanbanCardIds || []).length - validKanbanCardIds.length;
      toast.success(`Usunięto ${removedCount} nieistniejących ${removedCount === 1 ? 'kartę' : 'kart'} Kanban z zadania`);
      
      // Automatycznie zaktualizuj zadanie z oczyszczonymi kartami
      const updatedKanbanCards = (task.kanbanCards || []).filter(card => 
        validKanbanCardIds.includes(card.cardId)
      );
      
      updateTaskMutation.mutate({
        id: task._id,
        data: {
          kanbanCardIds: validKanbanCardIds,
          kanbanCards: updatedKanbanCards
        }
      });
    }
    
    setFormData({
      farmName: task.farmName,
      teamName: task.teamName,
      date: dateStr,
      time: timeStr,
      kanbanCardIds: validKanbanCardIds
    });
    setEditingTask(task);
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Admin i super admin mogą dodawać i edytować zadania
    if (user?.role !== 'super admin' && user?.role !== 'admin') {
      toast.error('Brak uprawnień do dodawania lub edycji zadań');
      return;
    }
    
    // Walidacja - sprawdź czy wszystkie wymagane pola są wypełnione
    if (!formData.farmName || !formData.teamName || !formData.date) {
      toast.error('Wszystkie pola są wymagane: nazwa farmy, nazwa ekipy, data');
      return;
    }
    
    // Połącz datę i godzinę w jeden obiekt Date
    const [hours, minutes] = formData.time.split(':').map(Number);
    const dateTime = new Date(formData.date);
    dateTime.setHours(hours, minutes, 0, 0);
    
    // Znajdź kolor ekipy - jeśli edytujemy zadanie i ekipa się zmieniła, pobierz nowy kolor
    const selectedTeam = teams.find(t => t.teamName?.trim() === formData.teamName?.trim());
    const teamColor = selectedTeam?.color || (editingTask?.teamColor || '#3B82F6');
    
    // Przygotuj informacje o kartach Kanban dla zapisania w bazie danych
    const kanbanCards = (formData.kanbanCardIds || []).map(cardId => {
      const cardInfo = findCard(cardId);
      if (cardInfo) {
        const listTitle = kanbanLists.find(l => l.id === cardInfo.listId)?.title || '';
        return {
          cardId: String(cardId),
          title: cardInfo.card.title || 'Bez tytułu',
          description: cardInfo.card.description || '',
          listTitle: listTitle
        };
      }
      // Jeśli karta nie została znaleziona, użyj tylko ID
      return {
        cardId: String(cardId),
        title: `Zadanie ${cardId}`,
        description: '',
        listTitle: ''
      };
    });
    
    const taskData = {
      farmName: formData.farmName,
      teamName: formData.teamName,
      teamColor: teamColor,
      date: dateTime.toISOString(),
      kanbanCardIds: formData.kanbanCardIds || [],
      kanbanCards: kanbanCards
    };
    
    console.log('Zapisywanie zadania:', taskData);
    
    if (editingTask) {
      updateTaskMutation.mutate({ id: editingTask._id, data: taskData });
    } else {
      addTaskMutation.mutate(taskData);
    }
  };

  const handleDelete = (taskId) => {
    // Tylko super admin może usuwać zadania
    if (user?.role !== 'super admin') {
      toast.error('Brak uprawnień do usuwania zadań');
      return;
    }
    
    if (window.confirm('Czy na pewno chcesz usunąć to zadanie?')) {
      deleteTaskMutation.mutate(taskId);
    }
  };

  // Funkcja pomocnicza do porównywania dat (bez strefy czasowej)
  // Formatuje datę w lokalnej strefie czasowej jako YYYY-MM-DD
  const formatDateLocal = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Funkcje do renderowania kalendarza
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { firstDay, lastDay, daysInMonth, startingDayOfWeek };
  };

  const getTasksForDate = (date) => {
    // Normalizuj datę do pełnej godziny 00:00:00 dla porównań
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    const dateStr = formatDateLocal(normalizedDate);
    return tasks.filter(task => {
      const taskDate = new Date(task.date);
      const taskDateStr = formatDateLocal(taskDate);
      return taskDateStr === dateStr;
    });
  };

  // Funkcja pomocnicza do określania koloru zadania
  const getTaskColorClasses = (task, isCompact = false) => {
    // Jeśli super admin i zadanie nie jest jego, użyj innego koloru (zielony) zamiast koloru ekipy
    if (user?.role === 'super admin' && task.createdBy?._id && task.createdBy._id.toString() !== user._id?.toString()) {
      // Zadania użytkowników - zielony kolor
      return isCompact 
        ? 'text-xs bg-green-100 text-green-800 px-1 py-0.5 rounded truncate cursor-move hover:bg-green-200 transition-opacity'
        : 'p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors cursor-move border border-green-200';
    }
    
    // Użyj koloru ekipy - zwróć tylko klasy bazowe, kolor będzie w stylu inline
    return isCompact
      ? 'text-xs px-1 py-0.5 rounded truncate cursor-move hover:opacity-80 transition-opacity text-white font-medium'
      : 'p-4 rounded-lg hover:opacity-90 transition-colors cursor-move border-2';
  };
  
  // Funkcja pomocnicza do uzyskania stylu inline dla koloru zadania
  const getTaskStyle = (task, isCompact = false) => {
    // Jeśli super admin i zadanie nie jest jego, nie ustawiaj koloru ekipy
    if (user?.role === 'super admin' && task.createdBy?._id && task.createdBy._id.toString() !== user._id?.toString()) {
      return {};
    }
    
    // Zawsze pobierz aktualny kolor z ekipy, jeśli ekipa istnieje
    // Używamy trim() aby usunąć ewentualne spacje i porównujemy dokładnie
    const selectedTeam = teams.find(t => t.teamName?.trim() === task.teamName?.trim());
    const taskColor = selectedTeam?.color || task.teamColor || '#3B82F6';
    
    // Debug log (można usunąć później)
    if (process.env.NODE_ENV === 'development') {
      console.log('Task color lookup:', {
        taskTeamName: task.teamName,
        selectedTeam: selectedTeam?.teamName,
        teamColor: selectedTeam?.color,
        taskColor: taskColor
      });
    }
    
    // Konwertuj hex na rgba dla lepszej kontroli przezroczystości
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    if (isCompact) {
      // Dla widoku compact użyj pełnego koloru jako tła (bez przezroczystości)
      return {
        backgroundColor: taskColor,
        borderColor: taskColor
      };
    }
    
    // Dla widoku normalnego użyj przezroczystości
    return {
      backgroundColor: hexToRgba(taskColor, 0.15),
      borderColor: hexToRgba(taskColor, 0.4),
      color: taskColor
    };
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setCurrentWeek(today);
    setSelectedDay(today);
  };

  const navigateWeek = (direction) => {
    setCurrentWeek(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + (direction * 7));
      return newDate;
    });
  };

  // Funkcje pomocnicze do obliczania tygodnia
  const getWeekDates = (date) => {
    const monday = new Date(date);
    const day = monday.getDay();
    const diff = monday.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    monday.setDate(diff);
    
    const week = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      week.push(d);
    }
    return week;
  };

  // Generuj godziny (6:00 - 22:00)
  const hours = Array.from({ length: 17 }, (_, i) => i + 6);

  // Funkcja pomocnicza do obliczania pozycji zadania w widoku tygodniowym
  const getTaskTimePosition = (task) => {
    const taskDate = new Date(task.date);
    const hours = taskDate.getHours();
    const minutes = taskDate.getMinutes();
    return (hours - 6) * 60 + minutes; // Pozycja w minutach od 6:00
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];
  const dayNames = ['Nie', 'Pon', 'Wto', 'Śro', 'Czw', 'Pią', 'Sob'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Widok: Kalendarz */}
      {activeSubmenu === 'calendar' && (
      <>
      {/* Kalendarz */}
      <div className="card">
        <div className="card-body">
          {/* Nawigacja */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => view === 'month' ? navigateMonth(-1) : navigateWeek(-1)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title={view === 'month' ? 'Poprzedni miesiąc' : 'Poprzedni tydzień'}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={goToToday}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Dzisiaj
              </button>
              <button
                onClick={() => view === 'month' ? navigateMonth(1) : navigateWeek(1)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title={view === 'month' ? 'Następny miesiąc' : 'Następny tydzień'}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <h3 className="text-xl font-semibold text-gray-900">
                {view === 'month' 
                  ? `${monthNames[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`
                  : (() => {
                      const weekDates = getWeekDates(currentWeek);
                      const start = weekDates[0];
                      const end = weekDates[6];
                      if (start.getMonth() === end.getMonth()) {
                        return `${start.getDate()}-${end.getDate()} ${monthNames[start.getMonth()]} ${start.getFullYear()}`;
                      } else {
                        return `${start.getDate()} ${monthNames[start.getMonth()]} - ${end.getDate()} ${monthNames[end.getMonth()]} ${start.getFullYear()}`;
                      }
                    })()
                }
              </h3>
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setView('month')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    view === 'month'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Miesiąc
                </button>
                <button
                  onClick={() => setView('week')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    view === 'week'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Tydzień
                </button>
              </div>
            </div>
          </div>

          {/* Legenda - tylko dla super adminów */}
          {user?.role === 'super admin' && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center mb-2">
                <span className="text-sm font-semibold text-gray-700">Legenda kolorów:</span>
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded mr-2"></div>
                  <span className="text-sm text-gray-600">Zadania admina</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-100 border border-green-200 rounded mr-2"></div>
                  <span className="text-sm text-gray-600">Zadania użytkowników</span>
                </div>
              </div>
            </div>
          )}

          {/* Widok miesięczny */}
          {view === 'month' && (
            <>
            <div className="grid grid-cols-7 gap-2 mb-4">
            {/* Nagłówki dni */}
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
                {day}
              </div>
            ))}

            {/* Puste komórki na początku miesiąca */}
            {Array.from({ length: startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1 }).map((_, i) => (
              <div key={`empty-${i}`} className="h-24"></div>
            ))}

            {/* Dni miesiąca */}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
              const dayTasks = getTasksForDate(currentDate);
              const isToday = currentDate.toDateString() === new Date().toDateString();

              const isSelected = currentDate.toDateString() === selectedDay.toDateString();
              
              const isDragOver = dragOverDay && dragOverDay.toDateString() === currentDate.toDateString();
              
              return (
                <div
                  key={day}
                  onClick={() => setSelectedDay(currentDate)}
                  onDragOver={(e) => handleDragOver(e, currentDate)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, currentDate)}
                  className={`h-24 border border-gray-200 rounded-lg p-2 overflow-y-auto cursor-pointer transition-all ${
                    isToday ? 'bg-blue-50 border-blue-300' : 
                    isSelected ? 'bg-green-50 border-green-300' : 
                    isDragOver ? 'bg-yellow-100 border-yellow-400 border-2' :
                    'bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>
                      {day}
                    </span>
                    <div className="flex items-center gap-1">
                      {dayTasks.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDateForRoute(currentDate);
                            setShowRouteModal(true);
                          }}
                          className="p-1 rounded hover:bg-green-100 text-green-600"
                          title="Wyznacz trasę dla tego dnia"
                        >
                          <Route className="h-3 w-3" />
                        </button>
                      )}
                      {(user?.role === 'super admin' || user?.role === 'admin') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openAddModal(currentDate);
                          }}
                          className="p-1 rounded hover:bg-blue-100 text-blue-600"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {dayTasks.slice(0, 3).map(task => (
                      <div
                        key={task._id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, task)}
                        onDragEnd={handleDragEnd}
                        className={getTaskColorClasses(task, true)}
                        style={getTaskStyle(task, true)}
                        onClick={(e) => {
                          // Jeśli klikamy, otwórz modal edycji tylko jeśli nie przeciągamy i jesteśmy adminem lub super adminem
                          if (!draggedTask && (user?.role === 'super admin' || user?.role === 'admin')) {
                            e.stopPropagation();
                            openEditModal(task);
                          } else if (!draggedTask && isUserInTeam(task.teamName)) {
                            // Jeśli użytkownik należy do ekipy, otwórz modal szczegółów z możliwością oznaczania realizacji
                            e.stopPropagation();
                            setSelectedTaskForDetails(task);
                            setShowTaskDetailsModal(true);
                          }
                        }}
                        title={
                          (user?.role === 'super admin' || user?.role === 'admin') && task.createdBy?.username
                            ? `${task.farmName} - ${task.teamName}\nDodane przez: ${task.createdBy.username}\n(Przeciągnij, aby przenieść lub kliknij aby edytować)`
                            : isUserInTeam(task.teamName)
                            ? `${task.farmName} - ${task.teamName}\nKliknij aby zobaczyć szczegóły i oznaczyć realizację zadań`
                            : `${task.farmName} - ${task.teamName}\n(Przeciągnij, aby przenieść)`
                        }
                      >
                        {task.farmName}
                      </div>
                    ))}
                    {dayTasks.length > 3 && (
                      <div className="text-xs text-gray-500 px-1">
                        +{dayTasks.length - 3} więcej
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
            
              {/* Przycisk dodawania zadania - tylko w widoku miesięcznym dla admina i super admina */}
              {(user?.role === 'super admin' || user?.role === 'admin') && (
                <button
                  onClick={() => openAddModal(selectedDay)}
                  className="w-full mt-4 btn-primary flex items-center justify-center"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Dodaj zadanie
                </button>
              )}
            </>
          )}

          {/* Widok tygodniowy */}
          {view === 'week' && (
            <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
              <div className="grid grid-cols-8 border-b border-gray-300">
                {/* Pusta komórka dla osi czasu */}
                <div className="border-r border-gray-300 bg-gray-50"></div>
                {/* Nagłówki dni tygodnia */}
                {getWeekDates(currentWeek).map((date, index) => {
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isSelected = date.toDateString() === selectedDay.toDateString();
                  return (
                    <div
                      key={index}
                      className={`border-r border-gray-300 last:border-r-0 py-2 px-3 text-center ${
                        index === 0 || index === 6 ? 'bg-gray-50' : 'bg-white'
                      } ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <div className="text-xs font-medium text-gray-600 mb-1">
                        {dayNames[index === 0 ? 6 : index - 1]}
                      </div>
                      <div className={`text-lg font-semibold ${
                        isToday 
                          ? 'bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto'
                          : 'text-gray-900'
                      }`}>
                        {date.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Siatka z godzinami */}
              <div className="overflow-x-auto">
                <div className="grid grid-cols-8" style={{ minHeight: '800px' }}>
                  {/* Oś czasu */}
                  <div className="border-r border-gray-300 bg-gray-50">
                    {hours.map((hour) => (
                      <div
                        key={hour}
                        className="border-b border-gray-200 h-12 flex items-start justify-end pr-2 pt-1"
                      >
                        <span className="text-xs text-gray-500">{hour}:00</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Kolumny dni */}
                  {getWeekDates(currentWeek).map((date, dayIndex) => {
                    const dayTasks = getTasksForDate(date);
                    const isToday = date.toDateString() === new Date().toDateString();
                    return (
                      <div
                        key={dayIndex}
                        className={`border-r border-gray-300 last:border-r-0 relative ${
                          dayIndex === 0 || dayIndex === 6 ? 'bg-gray-50' : 'bg-white'
                        } ${isToday ? 'bg-blue-50' : ''}`}
                        onClick={() => setSelectedDay(date)}
                      >
                        {/* Godziny */}
                        {hours.map((hour) => (
                          <div
                            key={hour}
                            className={`border-b border-gray-200 h-12 relative transition-colors ${
                              user?.role === 'super admin' 
                                ? 'cursor-pointer hover:bg-gray-100' 
                                : ''
                            }`}
                            onClick={(e) => {
                              // Tylko super admin może dodawać zadania przez kliknięcie
                              if (user?.role === 'super admin') {
                                // Jeśli kliknięto w pustą komórkę, otwórz modal z odpowiednią datą i godziną
                                if (e.target === e.currentTarget || e.target.classList.contains('hover:bg-gray-100')) {
                                  const targetDate = new Date(date);
                                  targetDate.setHours(hour, 0, 0, 0);
                                  openAddModal(targetDate);
                                }
                              }
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              const targetDate = new Date(date);
                              targetDate.setHours(hour, 0, 0, 0);
                              handleDragOver(e, targetDate);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const targetDate = new Date(date);
                              targetDate.setHours(hour, 0, 0, 0);
                              handleDrop(e, targetDate);
                            }}
                          >
                            {/* Zadania dla tego dnia i godziny */}
                            {dayTasks
                              .filter(task => {
                                const taskDate = new Date(task.date);
                                const taskHour = taskDate.getHours();
                                const taskMinutes = taskDate.getMinutes();
                                
                                // Jeśli zadanie ma godzinę 00:00 (bez ustawionej godziny), pokaż je w 6:00
                                if (taskHour === 0 && taskMinutes === 0) {
                                  return hour === 6;
                                }
                                
                                // W przeciwnym razie pokaż zadanie w jego godzinie lub w poprzedniej godzinie jeśli ma minuty > 0
                                return taskHour === hour || 
                                       (taskHour === hour - 1 && taskMinutes > 0);
                              })
                              .map((task, taskIndex) => {
                                const taskDate = new Date(task.date);
                                let taskHour = taskDate.getHours();
                                let taskMinutes = taskDate.getMinutes();
                                
                                // Jeśli zadanie nie ma ustawionej godziny (00:00), wyświetl je na początku 6:00
                                if (taskHour === 0 && taskMinutes === 0) {
                                  taskHour = 6;
                                  taskMinutes = 0;
                                }
                                
                                const isUserTask = user?.role === 'super admin' && task.createdBy?._id && task.createdBy._id.toString() !== user._id?.toString();
                                
                                // Oblicz ile zadań jest w tej godzinie
                                const tasksInThisHour = dayTasks.filter(t => {
                                  const td = new Date(t.date);
                                  const th = td.getHours();
                                  const tm = td.getMinutes();
                                  const normalizedTh = (th === 0 && tm === 0) ? 6 : th;
                                  return normalizedTh === hour || (normalizedTh === hour - 1 && tm > 0);
                                });
                                
                                const taskCount = tasksInThisHour.length;
                                const currentTaskPosition = tasksInThisHour.findIndex(t => t._id === task._id);
                                
                                // Pobierz aktualny kolor z ekipy - użyj trim() dla bezpieczeństwa
                                const selectedTeam = teams.find(t => t.teamName?.trim() === task.teamName?.trim());
                                
                                return (
                                  <div
                                    key={task._id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, task)}
                                    onDragEnd={handleDragEnd}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Admin i super admin mogą edytować zadania
                                      if (!draggedTask && (user?.role === 'super admin' || user?.role === 'admin')) {
                                        openEditModal(task);
                                      } else if (!draggedTask && isUserInTeam(task.teamName)) {
                                        // Jeśli użytkownik należy do ekipy, otwórz modal szczegółów z możliwością oznaczania realizacji
                                        setSelectedTaskForDetails(task);
                                        setShowTaskDetailsModal(true);
                                      }
                                    }}
                                    className={`absolute px-2 py-1 rounded text-xs cursor-move truncate z-10 text-white hover:opacity-80 ${
                                      draggedTask?._id === task._id ? 'opacity-50' : ''
                                    }`}
                                    style={{
                                      top: `${(taskMinutes / 60) * 48}px`,
                                      height: '48px',
                                      width: taskCount > 1 ? `calc(${100 / taskCount}% - 2px)` : 'calc(100% - 8px)',
                                      left: taskCount > 1 ? `calc(${(currentTaskPosition * 100) / taskCount}% + 1px)` : '4px',
                                      right: 'auto',
                                      backgroundColor: isUserTask ? '#10b981' : (selectedTeam?.color || task.teamColor || '#3B82F6'),
                                      borderColor: isUserTask ? '#059669' : (selectedTeam?.color || task.teamColor || '#2563EB')
                                    }}
                                    title={`${task.farmName} - ${task.teamName}${(user?.role === 'super admin' || user?.role === 'admin') && task.createdBy?.username ? `\nDodane przez: ${task.createdBy.username}` : ''}`}
                                  >
                                    <div className="font-medium">{task.farmName}</div>
                                    <div className="text-xs opacity-90">{task.teamName}</div>
                                  </div>
                                );
                              })}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lista zadań dla wybranego dnia */}
      {selectedDay && (
        <div className="card">
          <div className="card-header bg-gradient-to-r from-green-50 to-emerald-50">
            <h3 className="text-lg font-semibold text-gray-900">
              Zadania na {selectedDay.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}
            </h3>
            {/* Debug info dla użytkowników z ekipy */}
            {user?.role !== 'super admin' && user?.role !== 'admin' && (
              <p className="text-xs text-gray-600 mt-1">
                {(() => {
                  if (!teams || teams.length === 0) {
                    return 'Ładowanie ekip...';
                  }
                  
                  if (!user || !user._id) {
                    return 'Brak danych użytkownika';
                  }
                  
                  const userId = user._id.toString();
                  const userTeams = teams.filter(t => {
                    if (!t.firstServicePerson && !t.secondServicePerson) return false;
                    
                    const firstPersonId = t.firstServicePerson?._id?.toString() || t.firstServicePerson?.toString();
                    const secondPersonId = t.secondServicePerson?._id?.toString() || t.secondServicePerson?.toString();
                    
                    console.log('Sprawdzanie ekipy:', {
                      teamName: t.teamName,
                      userId,
                      firstPersonId,
                      secondPersonId,
                      firstPerson: t.firstServicePerson,
                      secondPerson: t.secondServicePerson
                    });
                    
                    return firstPersonId === userId || secondPersonId === userId;
                  }).map(t => t.teamName);
                  
                  return userTeams.length > 0 
                    ? `Twoje ekipy: ${userTeams.join(', ')}`
                    : 'Nie jesteś przypisany do żadnej ekipy';
                })()}
              </p>
            )}
          </div>
          <div className="card-body">
            {getTasksForDate(selectedDay).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>Brak zadań na ten dzień</p>
              </div>
            ) : (
              <div className="space-y-3">
                {getTasksForDate(selectedDay).map(task => (
                  <div
                    key={task._id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task)}
                    onDragEnd={handleDragEnd}
                    className={getTaskColorClasses(task, false)}
                    style={getTaskStyle(task)}
                    title={
                      (user?.role === 'super admin' || user?.role === 'admin') && task.createdBy?.username
                        ? `Przeciągnij zadanie na inny dzień w kalendarzu lub kliknij aby edytować`
                        : isUserInTeam(task.teamName)
                        ? `Kliknij aby zobaczyć szczegóły i oznaczyć realizację zadań`
                        : 'Przeciągnij zadanie na inny dzień w kalendarzu'
                    }
                    onClick={() => {
                      if (user?.role === 'super admin' || user?.role === 'admin') {
                        openEditModal(task);
                      } else if (isUserInTeam(task.teamName)) {
                        setSelectedTaskForDetails(task);
                        setShowTaskDetailsModal(true);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Clock className="h-4 w-4 text-orange-600 mr-2" />
                          <span className="text-sm font-medium text-gray-700">
                            {new Date(task.date).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="flex items-center mb-2">
                          <Building2 className="h-4 w-4 text-green-600 mr-2" />
                          <span className="font-semibold text-gray-900">{task.farmName}</span>
                        </div>
                        <div className="flex items-center mb-2">
                          <Users className="h-4 w-4 text-blue-600 mr-2" />
                          <span className="text-gray-700">{task.teamName}</span>
                        </div>
                        {(user?.role === 'super admin' || user?.role === 'admin') && task.createdBy?.username && (
                          <div className="flex items-center mt-2 pt-2 border-t border-gray-200">
                            <User className="h-4 w-4 text-purple-600 mr-2" />
                            <span className="text-xs text-gray-500">
                              Dodane przez: <span className="font-medium text-gray-700">{task.createdBy.username}</span>
                            </span>
                          </div>
                        )}
                      </div>
                      {(user?.role === 'super admin' || user?.role === 'admin') && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditModal(task)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(task._id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal do dodawania/edycji zadania */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingTask ? 'Edytuj zadanie' : 'Dodaj zadanie'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingTask(null);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Godzina
                </label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Ustaw godzinę rozpoczęcia zadania
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nazwa farmy
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={formData.farmName}
                    onChange={(e) => setFormData({ ...formData, farmName: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Wybierz farmę</option>
                    {farms.map(farm => (
                      <option key={farm._id} value={farm.farmName}>
                        {farm.farmName}
                      </option>
                    ))}
                  </select>
                  {(user?.role === 'super admin' || user?.role === 'admin' || user?.moduleAccess?.planner) && (
                    <button
                      type="button"
                      onClick={() => {
                        const currentFarmName = formData.farmName;
                        openAddFarmModal((newFarmName) => {
                          // Po dodaniu farmy, otwórz ponownie modal zadania z wybraną farmą
                          setFormData({ ...formData, farmName: newFarmName });
                          setShowModal(true);
                        });
                      }}
                      className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-300 transition-colors"
                      title="Dodaj nową farmę"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  )}
                </div>
                {farms.length === 0 && (
                  <p className="mt-1 text-xs text-orange-600">
                    Brak farm. Dodaj farmę w sekcji "Farmy Foto".
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nazwa ekipy obsługującej
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    value={formData.teamName}
                    onChange={(e) => {
                      const selectedTeamName = e.target.value;
                      const selectedTeam = teams.find(t => t.teamName === selectedTeamName);
                      setFormData({ ...formData, teamName: selectedTeamName });
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Wybierz ekipę</option>
                    {teams.map(team => (
                      <option key={team._id} value={team.teamName}>
                        {team.teamName}
                      </option>
                    ))}
                  </select>
                  {(user?.role === 'super admin' || user?.role === 'admin' || user?.moduleAccess?.planner) && (
                    <button
                      type="button"
                      onClick={() => {
                        const currentTeamName = formData.teamName;
                        openAddTeamModal((newTeamName) => {
                          // Po dodaniu ekipy, otwórz ponownie modal zadania z wybraną ekipą
                          setFormData({ ...formData, teamName: newTeamName });
                          setShowModal(true);
                        });
                      }}
                      className="px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-300 transition-colors"
                      title="Dodaj nową ekipę"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  )}
                </div>
                {teams.length === 0 && (
                  <p className="mt-1 text-xs text-orange-600">
                    Brak ekip. Dodaj ekipę w sekcji "Ekipa Serwisowa".
                  </p>
                )}
              </div>

              {/* Wybór zadań z Kanban */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zadania z Kanban (opcjonalnie)
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-2">
                  {kanbanLists.flatMap(list => list.cards).length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-2">
                      Brak kart w Kanban. Dodaj karty w sekcji Kanban.
                    </p>
                  ) : (
                    kanbanLists.flatMap(list => 
                      list.cards.map(card => (
                        <label
                          key={card.id}
                          className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.kanbanCardIds?.some(id => String(id) === String(card.id)) || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  kanbanCardIds: [...(formData.kanbanCardIds || []), card.id]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  kanbanCardIds: (formData.kanbanCardIds || []).filter(id => String(id) !== String(card.id))
                                });
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 flex-1">
                            {card.title}
                            <span className="text-xs text-gray-500 ml-2">
                              ({list.title})
                            </span>
                          </span>
                        </label>
                      ))
                    )
                  )}
                </div>
                {formData.kanbanCardIds?.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Wybrano {formData.kanbanCardIds.length} {formData.kanbanCardIds.length === 1 ? 'zadanie' : 'zadań'}
                  </p>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTask(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={addTaskMutation.isLoading || updateTaskMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {addTaskMutation.isLoading || updateTaskMutation.isLoading
                    ? 'Zapisywanie...'
                    : editingTask
                    ? 'Zapisz zmiany'
                    : 'Dodaj zadanie'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </>
      )}

      {/* Modal szczegółów zadania z możliwością oznaczania realizacji */}
      {showTaskDetailsModal && selectedTaskForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Szczegóły zadania
              </h3>
              <button
                onClick={() => {
                  setShowTaskDetailsModal(false);
                  setSelectedTaskForDetails(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Informacje podstawowe */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Farma
                  </label>
                  <div className="flex items-center">
                    <Building2 className="h-4 w-4 text-green-600 mr-2" />
                    <span className="text-gray-900">{selectedTaskForDetails.farmName}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ekipa
                  </label>
                  <div className="flex items-center">
                    <Users className="h-4 w-4 text-blue-600 mr-2" />
                    <span className="text-gray-900">{selectedTaskForDetails.teamName}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data
                  </label>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-orange-600 mr-2" />
                    <span className="text-gray-900">
                      {new Date(selectedTaskForDetails.date).toLocaleDateString('pl-PL', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Godzina
                  </label>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-purple-600 mr-2" />
                    <span className="text-gray-900">
                      {new Date(selectedTaskForDetails.date).toLocaleTimeString('pl-PL', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Zadania z Kanban */}
              {selectedTaskForDetails.kanbanCardIds && selectedTaskForDetails.kanbanCardIds.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Zadania do wykonania
                  </label>
                  <div className="space-y-2">
                    {selectedTaskForDetails.kanbanCardIds.map(kanbanCardId => {
                      const isCompleted = isTaskCompletedByUser(selectedTaskForDetails, kanbanCardId);
                      
                      // Najpierw spróbuj znaleźć kartę w danych z API (kanbanLists)
                      let cardInfo = null;
                      let cardChecklists = [];
                      
                      // Szukaj karty w wszystkich listach z API
                      for (const list of kanbanLists) {
                        const card = list.cards.find(c => String(c.id) === String(kanbanCardId));
                        if (card) {
                          cardInfo = { card, listId: list.id };
                          cardChecklists = card.checklists || [];
                          break;
                        }
                      }
                      
                      // Jeśli nie znaleziono w API, spróbuj w localStorage (fallback)
                      if (!cardInfo) {
                        cardInfo = findCard(kanbanCardId);
                        if (cardInfo) {
                          cardChecklists = cardInfo.card?.checklists || [];
                        }
                      }
                      
                      // Jeśli karta nie jest w localStorage ani w API, użyj zapisanych informacji z bazy danych
                      const savedCardInfo = selectedTaskForDetails.kanbanCards?.find(
                        c => String(c.cardId) === String(kanbanCardId)
                      );
                      
                      // Użyj informacji z API/localStorage jeśli dostępne, w przeciwnym razie z bazy danych
                      const displayTitle = cardInfo?.card?.title || savedCardInfo?.title || `Zadanie ${kanbanCardId}`;
                      const displayDescription = cardInfo?.card?.description || savedCardInfo?.description || '';
                      const displayListTitle = cardInfo?.listId 
                        ? (kanbanLists.find(l => l.id === cardInfo.listId)?.title || '')
                        : (savedCardInfo?.listTitle || '');

                      return (
                        <div
                          key={kanbanCardId}
                          className={`p-4 border-2 rounded-lg ${
                            isCompleted
                              ? 'bg-green-50 border-green-300'
                              : cardInfo 
                                ? 'bg-white border-gray-200'
                                : 'bg-blue-50 border-blue-200'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center mb-1">
                                <span className="font-medium text-gray-900">
                                  {displayTitle}
                                </span>
                                {displayListTitle && (
                                  <span className="ml-2 text-xs text-gray-500">
                                    ({displayListTitle})
                                  </span>
                                )}
                              </div>
                              {displayDescription && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {displayDescription}
                                </p>
                              )}
                              {!cardInfo && savedCardInfo && (
                                <p className="text-xs text-blue-600 mt-1">
                                  Podstawowe informacje z bazy danych (pełne dane karty dostępne po odświeżeniu)
                                </p>
                              )}
                              {!cardInfo && !savedCardInfo && (
                                <p className="text-xs text-red-600 mt-1 flex items-center">
                                  <X className="h-3 w-3 mr-1" />
                                  Karta nie istnieje w Kanban (stare dane testowe)
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {(user?.role === 'super admin' || user?.role === 'admin') && (
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Czy na pewno chcesz usunąć kartę "${displayTitle}" z tego zadania?`)) {
                                      const updatedKanbanCardIds = selectedTaskForDetails.kanbanCardIds.filter(id => String(id) !== String(kanbanCardId));
                                      const updatedKanbanCards = selectedTaskForDetails.kanbanCards?.filter(c => String(c.cardId) !== String(kanbanCardId)) || [];
                                      
                                      updateTaskMutation.mutate({
                                        id: selectedTaskForDetails._id,
                                        data: {
                                          kanbanCardIds: updatedKanbanCardIds,
                                          kanbanCards: updatedKanbanCards
                                        }
                                      });
                                      
                                      // Zaktualizuj lokalnie dla natychmiastowego odświeżenia
                                      setSelectedTaskForDetails({
                                        ...selectedTaskForDetails,
                                        kanbanCardIds: updatedKanbanCardIds,
                                        kanbanCards: updatedKanbanCards
                                      });
                                    }
                                  }}
                                  className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors flex items-center space-x-1"
                                  title="Usuń kartę z zadania"
                                >
                                  <Trash2 className="h-3 w-3" />
                                  <span>Usuń</span>
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  completeTaskMutation.mutate({
                                    taskId: selectedTaskForDetails._id,
                                    kanbanCardId: kanbanCardId
                                  });
                                }}
                                disabled={completeTaskMutation.isLoading}
                                className={`px-3 py-1.5 rounded-lg transition-colors flex items-center space-x-1 text-sm ${
                                  isCompleted
                                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    : 'bg-green-600 text-white hover:bg-green-700'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                {isCompleted ? (
                                  <>
                                    <X className="h-3.5 w-3.5" />
                                    <span>Cofnij kartę</span>
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                    <span>Oznacz kartę</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Elementy checklistów */}
                          {cardChecklists.length > 0 ? (
                            <div className="mt-3 space-y-3 border-t pt-3">
                              {cardChecklists.map(checklist => (
                                <div key={checklist.id} className="space-y-2">
                                  <div className="flex items-center mb-1">
                                    <CheckSquare className="h-4 w-4 text-gray-500 mr-2" />
                                    <span className="text-sm font-semibold text-gray-700">
                                      {checklist.title}
                                    </span>
                                  </div>
                                  {checklist.items && checklist.items.length > 0 ? (
                                    <div className="ml-6 space-y-1.5">
                                      {checklist.items.map(item => {
                                        const isItemCompleted = isChecklistItemCompletedByUser(
                                          selectedTaskForDetails,
                                          kanbanCardId,
                                          checklist.id,
                                          item.id
                                        );
                                        return (
                                          <div
                                            key={item.id}
                                            className={`flex items-center space-x-2 p-2 rounded ${
                                              isItemCompleted ? 'bg-green-50' : 'bg-gray-50'
                                            }`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isItemCompleted}
                                              onChange={() => {
                                                completeChecklistItemMutation.mutate({
                                                  taskId: selectedTaskForDetails._id,
                                                  kanbanCardId: kanbanCardId,
                                                  checklistId: checklist.id,
                                                  itemId: item.id
                                                });
                                              }}
                                              disabled={completeChecklistItemMutation.isLoading}
                                              className="h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50"
                                            />
                                            <span
                                              className={`flex-1 text-sm ${
                                                isItemCompleted
                                                  ? 'line-through text-gray-500'
                                                  : 'text-gray-900'
                                              }`}
                                            >
                                              {item.title}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <p className="ml-6 text-xs text-gray-400">
                                      Brak elementów
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs text-gray-400 text-center">
                                Brak checklistów w tej karcie
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-sm text-gray-500">
                    Brak przypisanych zadań z Kanban dla tego wyjazdu.
                  </p>
                </div>
              )}

              {/* Mapa z trasą dojazdu */}
              {(() => {
                const currentFarm = farms.find(f => f.farmName === selectedTaskForDetails.farmName);
                
                // Jeśli farma nie ma lokalizacji, pokaż komunikat
                if (!currentFarm || !currentFarm.location) {
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        <MapPin className="h-4 w-4 inline mr-2" />
                        Lokalizacja farmy
                      </label>
                      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-sm text-yellow-800">
                          Brak współrzędnych GPS dla farmy <strong>{selectedTaskForDetails.farmName}</strong>. 
                          Dodaj lokalizację farmy w sekcji "Farmy Foto" aby zobaczyć mapę.
                        </p>
                      </div>
                    </div>
                  );
                }

                const taskDate = new Date(selectedTaskForDetails.date);
                const taskDateStart = new Date(taskDate);
                taskDateStart.setHours(0, 0, 0, 0);
                const taskDateEnd = new Date(taskDate);
                taskDateEnd.setHours(23, 59, 59, 999);

                const sameDayTasks = tasks.filter(task => {
                  const taskDate = new Date(task.date);
                  return task.teamName === selectedTaskForDetails.teamName &&
                         taskDate >= taskDateStart &&
                         taskDate <= taskDateEnd &&
                         task._id !== selectedTaskForDetails._id;
                });

                const hasMultipleFarms = sameDayTasks.length > 0;

                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <MapPin className="h-4 w-4 inline mr-2" />
                      {hasMultipleFarms ? 'Trasa dojazdu między farmami' : 'Lokalizacja farmy'}
                    </label>
                    <div 
                      ref={taskDetailsMapRef} 
                      style={{ width: '100%', height: '400px' }} 
                      className="rounded-lg border border-gray-300 bg-gray-100"
                    />
                    {hasMultipleFarms && (
                      <p className="text-xs text-gray-500 mt-2">
                        Trasa obejmuje wszystkie farmy zaplanowane dla ekipy <strong>{selectedTaskForDetails.teamName}</strong> w tym dniu.
                      </p>
                    )}
                    {routeInfo && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 text-blue-600 mr-2" />
                              <span className="text-sm font-medium text-gray-700">
                                Czas dojazdu: <span className="text-blue-700">{routeInfo.duration}</span>
                              </span>
                            </div>
                            <div className="flex items-center">
                              <MapPin className="h-4 w-4 text-blue-600 mr-2" />
                              <span className="text-sm font-medium text-gray-700">
                                Dystans: <span className="text-blue-700">{routeInfo.distance} km</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowTaskDetailsModal(false);
                  setSelectedTaskForDetails(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal wyznaczania trasy dla danego dnia */}
      {showRouteModal && selectedDateForRoute && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <Route className="h-5 w-5 mr-2 text-green-600" />
                Wyznacz trasę - {selectedDateForRoute.toLocaleDateString('pl-PL', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h3>
              <button
                onClick={() => {
                  setShowRouteModal(false);
                  setSelectedDateForRoute(null);
                  setRouteModalRouteInfo(null);
                  setUseManualOrigin(false);
                  setManualOrigin('');
                  setManualOriginPlace(null);
                  setGoogleMapsError(null);
                  // Wyczyść timeout
                  if (manualOriginTimeoutRef.current) {
                    clearTimeout(manualOriginTimeoutRef.current);
                    manualOriginTimeoutRef.current = null;
                  }
                  // Zresetuj flagę inicjalizacji
                  isInitializingRouteRef.current = false;
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              {/* Opcja ręcznego wprowadzenia punktu początkowego */}
              <div className="mb-6">
                <label className="flex items-center cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={useManualOrigin}
                    onChange={(e) => {
                      setUseManualOrigin(e.target.checked);
                      setRouteModalRouteInfo(null);
                      // Zresetuj flagę inicjalizacji aby pozwolić na ponowną inicjalizację
                      isInitializingRouteRef.current = false;
                      if (!e.target.checked) {
                        setManualOrigin('');
                        setManualOriginPlace(null);
                      }
                    }}
                    className="h-4 w-4 text-green-600 rounded focus:ring-2 focus:ring-green-500 cursor-pointer"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Wprowadź punkt początkowy ręcznie
                  </span>
                </label>
                
                {useManualOrigin && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="bg-green-600 text-white rounded-full w-6 h-6 inline-flex items-center justify-center text-xs font-bold mr-2">1</span>
                      Punkt początkowy (skąd)
                    </label>
                    <div className="flex gap-2">
                      <input
                        ref={originAutocompleteRef}
                        type="text"
                        value={manualOrigin}
                        onChange={(e) => {
                          const value = e.target.value;
                          setManualOrigin(value);
                          // Gdy użytkownik zmienia tekst, wyczyść manualOriginPlace aby wymusić ponowne geokodowanie
                          if (manualOriginPlace) {
                            setManualOriginPlace(null);
                          }
                          // Czyszczenie timeoutu dla debounce geokodowania
                          if (manualOriginTimeoutRef.current) {
                            clearTimeout(manualOriginTimeoutRef.current);
                            manualOriginTimeoutRef.current = null;
                          }
                        }}
                        onKeyDown={async (e) => {
                          // Gdy użytkownik naciśnie Enter, geokoduj od razu
                          if (e.key === 'Enter' && manualOrigin) {
                            e.preventDefault();
                            await handleGeocodeAndRoute();
                          }
                        }}
                        placeholder="Wpisz adres lub miejsce..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      <button
                        onClick={async () => {
                          await handleGeocodeAndRoute();
                        }}
                        disabled={!manualOrigin || !manualOrigin.trim()}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        title={manualOrigin && manualOrigin.trim() ? 'Kliknij aby geokodować adres i wyznaczyć trasę' : 'Wpisz adres aby wyznaczyć trasę'}
                      >
                        <Route className="h-4 w-4" />
                        Wyznacz trasę
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Lista farm z zadań */}
              {(() => {
                const dayTasks = getTasksForDate(selectedDateForRoute);
                const farmLocations = [];
                dayTasks.forEach(task => {
                  const farm = farms.find(f => f.farmName === task.farmName);
                  if (farm && farm.location) {
                    farmLocations.push({ farm, task });
                  }
                });

                if (farmLocations.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p>Brak zadań z lokalizacjami GPS dla tego dnia.</p>
                      {!useManualOrigin && (
                        <p className="text-sm mt-2 text-gray-400">Włącz opcję ręcznego wprowadzenia punktu początkowego, aby wyznaczyć trasę.</p>
                      )}
                    </div>
                  );
                }

                if (farmLocations.length === 1 && !useManualOrigin) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p>Jest tylko jedna farma dla tego dnia. Trasa wymaga co najmniej dwóch lokalizacji.</p>
                      <p className="text-sm mt-2 text-gray-400">Włącz opcję ręcznego wprowadzenia punktu początkowego, aby wyznaczyć trasę.</p>
                    </div>
                  );
                }

                return (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Punkty trasy z zadań:
                    </label>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {farmLocations.map((item, index) => (
                        <div key={index} className="flex items-center px-3 py-2 bg-gray-50 rounded-lg">
                          <span className={`rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-2 ${
                            index === 0 && !useManualOrigin ? 'bg-green-600' : 
                            index === farmLocations.length - 1 ? 'bg-red-600' : 
                            'bg-blue-600'
                          } text-white`}>
                            {useManualOrigin ? index + 2 : index + 1}
                          </span>
                          <span className="text-sm font-medium text-gray-700">{item.farm.farmName}</span>
                          {item.task.teamName && (
                            <span className="ml-2 text-xs text-gray-500">({item.task.teamName})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Komunikat błędu Google Maps */}
              {googleMapsError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <X className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-red-800">Błąd Google Maps</h3>
                      <div className="mt-1 text-sm text-red-700 whitespace-pre-line">
                        {googleMapsError.split(/(?=\d+\.)/).map((part, index) => (
                          part.trim() ? (
                            <p key={index} className={index > 0 ? 'mt-1' : ''}>{part.trim()}</p>
                          ) : null
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-red-600">
                        Sprawdź konsolę przeglądarki (F12) dla szczegółów.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Mapa z trasą */}
              <div className="mb-4">
                <div 
                  ref={routeModalMapRef} 
                  style={{ width: '100%', height: '500px' }} 
                  className="rounded-lg border border-gray-300 bg-gray-100"
                />
              </div>

              {/* Informacje o trasie */}
              {routeModalRouteInfo && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-green-600 mr-2" />
                        <span className="text-sm font-medium text-gray-700">
                          Czas dojazdu: <span className="text-green-700 font-semibold">{routeModalRouteInfo.duration}</span>
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Route className="h-5 w-5 text-green-600 mr-2" />
                        <span className="text-sm font-medium text-gray-700">
                          Dystans: <span className="text-green-700 font-semibold">{routeModalRouteInfo.distance} km</span>
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={handleShareRoute}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      Udostępnij trasę
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    {useManualOrigin 
                      ? `Trasa zaczyna się w ${manualOrigin} i prowadzi przez wszystkie farmy zaplanowane na ${selectedDateForRoute.toLocaleDateString('pl-PL', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}.`
                      : `Trasa obejmuje wszystkie farmy zaplanowane na ${selectedDateForRoute.toLocaleDateString('pl-PL', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}.`
                    }
                  </p>
                </div>
              )}

              {/* Loading indicator */}
              {!routeModalRouteInfo && (
                <div className="text-center py-4 text-gray-500">
                  <LoadingSpinner />
                  <p className="mt-2">Obliczanie trasy...</p>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => {
                  setShowRouteModal(false);
                  setSelectedDateForRoute(null);
                  setRouteModalRouteInfo(null);
                  setUseManualOrigin(false);
                  setManualOrigin('');
                  setManualOriginPlace(null);
                  setGoogleMapsError(null);
                  // Wyczyść timeout
                  if (manualOriginTimeoutRef.current) {
                    clearTimeout(manualOriginTimeoutRef.current);
                    manualOriginTimeoutRef.current = null;
                  }
                  // Zresetuj flagę inicjalizacji
                  isInitializingRouteRef.current = false;
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Widok: Ekipa Serwisowe */}
      {activeSubmenu === 'teams' && (
        <div className="card">
          <div className="card-header bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <Wrench className="h-5 w-5 mr-2" />
                Ekipa Serwisowa
              </h3>
              {(user?.role === 'super admin' || user?.role === 'admin' || user?.moduleAccess?.planner) && (
                <button
                  onClick={openAddTeamModal}
                  className="btn-primary flex items-center"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Dodaj ekipę
                </button>
              )}
            </div>
          </div>
          <div className="card-body">
            {teamsLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : teams.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Wrench className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>Brak ekip serwisowych</p>
              </div>
            ) : (
              <div className="space-y-3">
                {teams.map(team => {
                  // Użyj koloru z ekipy lub domyślnego
                  const teamColor = team.color || '#3B82F6';
                  console.log('Rendering team:', team.teamName, 'with color:', teamColor);
                  
                  return (
                  <div key={team._id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Users className="h-4 w-4 text-blue-600 mr-2" />
                          <span className="font-semibold text-gray-900">{team.teamName}</span>
                          <div 
                            className="ml-3 w-6 h-6 rounded-full border-2 border-gray-300"
                            style={{ backgroundColor: teamColor }}
                            title={`Kolor ekipy: ${teamColor}`}
                          />
                        </div>
                        <div className="flex items-center mb-2">
                          <User className="h-4 w-4 text-green-600 mr-2" />
                          <span className="text-gray-700">
                            {(() => {
                              // Jeśli jest obiektem z username (populate'owany)
                              if (team.firstServicePerson?.username) {
                                return team.firstServicePerson.username;
                              }
                              // Jeśli jest stringiem (ObjectId), szukaj w liście użytkowników
                              if (typeof team.firstServicePerson === 'string') {
                                const foundUser = allUsers.find(u => u._id === team.firstServicePerson);
                                return foundUser?.username || 'Ładowanie...';
                              }
                              // Jeśli jest obiektem z _id (ale bez username), szukaj w liście
                              if (team.firstServicePerson?._id) {
                                const foundUser = allUsers.find(u => u._id === team.firstServicePerson._id.toString());
                                return foundUser?.username || 'Ładowanie...';
                              }
                              return 'Nieznany użytkownik';
                            })()}
                          </span>
                        </div>
                        {team.secondServicePerson && (
                          <div className="flex items-center mb-2">
                            <User className="h-4 w-4 text-orange-600 mr-2" />
                            <span className="text-gray-700">
                              {(() => {
                                // Jeśli jest obiektem z username (populate'owany)
                                if (team.secondServicePerson?.username) {
                                  return team.secondServicePerson.username;
                                }
                                // Jeśli jest stringiem (ObjectId), szukaj w liście użytkowników
                                if (typeof team.secondServicePerson === 'string') {
                                  const foundUser = allUsers.find(u => u._id === team.secondServicePerson);
                                  return foundUser?.username || 'Ładowanie...';
                                }
                                // Jeśli jest obiektem z _id (ale bez username), szukaj w liście
                                if (team.secondServicePerson?._id) {
                                  const foundUser = allUsers.find(u => u._id === team.secondServicePerson._id.toString());
                                  return foundUser?.username || 'Ładowanie...';
                                }
                                return 'Nieznany użytkownik';
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                      {(user?.role === 'super admin' || user?.role === 'admin' || user?.moduleAccess?.planner) && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditTeamModal(team)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTeam(team._id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Widok: Farmy Foto */}
      {activeSubmenu === 'farms' && (
        <div className="card">
          <div className="card-header bg-gradient-to-r from-green-50 to-teal-50">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <Camera className="h-5 w-5 mr-2" />
                Farmy Foto
              </h3>
              {(user?.role === 'super admin' || user?.role === 'admin') && (
                <button
                  onClick={openAddFarmModal}
                  className="btn-primary flex items-center"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Dodaj farmę
                </button>
              )}
            </div>
          </div>
          <div className="card-body">
            {farmsError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-semibold">Błąd pobierania farm</p>
                <p className="text-red-600 text-sm mt-1">
                  {farmsError.response?.data?.message || farmsError.message || 'Nie udało się pobrać farm z serwera'}
                </p>
                {farmsError.response?.status === 403 && (
                  <p className="text-red-600 text-sm mt-2">
                    Brak uprawnień do modułu Farmy Foto. Skontaktuj się z administratorem.
                  </p>
                )}
              </div>
            )}
            {farmsLoading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : farms.length === 0 && !farmsError ? (
              <div className="text-center py-8 text-gray-500">
                <Camera className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>Brak farm</p>
              </div>
            ) : (
              <div className="space-y-3">
                {farms.map(farm => (
                  <div key={farm._id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Building2 className="h-4 w-4 text-green-600 mr-2" />
                          <span className="font-semibold text-gray-900">{farm.farmName}</span>
                        </div>
                        {farm.location && (
                          <div className="flex items-center mb-2">
                            <MapPin className="h-4 w-4 text-red-600 mr-2" />
                            <span className="text-gray-700">
                              Lat: {farm.location.latitude.toFixed(6)}, Lng: {farm.location.longitude.toFixed(6)}
                            </span>
                          </div>
                        )}
                      </div>
                      {(user?.role === 'super admin' || user?.role === 'admin') && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openEditFarmModal(farm)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteFarm(farm._id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Widok: Kanban Board */}
      {activeSubmenu === 'kanban' && (
        <div className="w-full">
          <div className="flex space-x-3 overflow-x-auto pb-4 items-start">
            {/* Listy Kanban */}
            {kanbanLists.map((list) => (
              <div
                key={list.id}
                className={`flex flex-col flex-shrink-0 w-64 bg-gray-100 rounded-lg p-3 transition-colors shadow-sm ${
                  dragOverList === list.id ? 'bg-blue-100 border-2 border-blue-400' : ''
                }`}
                onDragOver={(e) => handleKanbanListDragOver(e, list.id)}
                onDragLeave={handleKanbanListDragLeave}
                onDrop={(e) => handleKanbanListDrop(e, list.id)}
              >
                {/* Nagłówek listy */}
                <div className="flex items-center justify-between mb-3 relative">
                  {editingListTitle === list.id ? (
                    <input
                      type="text"
                      value={editingListTitleText}
                      onChange={(e) => setEditingListTitleText(e.target.value)}
                      onBlur={() => {
                        // Zapisz zmiany
                        if (editingListTitleText.trim() && editingListTitleText !== list.title) {
                          updateListMutation.mutate({
                            id: list.id,
                            data: { title: editingListTitleText.trim() }
                          });
                        }
                        setEditingListTitle(null);
                        setEditingListTitleText('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.target.blur();
                        }
                        if (e.key === 'Escape') {
                          setEditingListTitle(null);
                          setEditingListTitleText('');
                        }
                      }}
                      autoFocus
                      className="flex-1 text-sm font-semibold text-gray-900 px-2 py-1 border-2 border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <h3 
                      className="text-sm font-semibold text-gray-900 cursor-text select-text hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        setEditingListTitle(list.id);
                        setEditingListTitleText(list.title);
                      }}
                    >
                      {list.title}
                    </h3>
                  )}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowListMenu(showListMenu === list.id ? null : list.id);
                      }}
                      className="p-1 rounded hover:bg-gray-200 transition-colors text-gray-600"
                      title="Opcje listy"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    
                    {/* Menu opcji listy */}
                    {showListMenu === list.id && (
                      <>
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={() => setShowListMenu(null)}
                        />
                        <div className="absolute right-0 top-8 z-20 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[120px]">
                          <button
                            onClick={() => {
                              if (window.confirm(`Czy na pewno chcesz usunąć listę "${list.title}"?`)) {
                                deleteListMutation.mutate(list.id);
                                setShowListMenu(null);
                              }
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Usuń listę
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Obszar na karty */}
                <div className={`flex-1 space-y-2.5 bg-transparent rounded-lg mb-2 transition-colors ${
                  dragOverList === list.id ? 'bg-blue-50 min-h-[100px]' : ''
                }`}>
                  {list.cards.length === 0 ? (
                    <div className="text-center py-4 text-gray-400">
                      <p className="text-xs">
                        {dragOverList === list.id ? 'Upuść kartę tutaj' : 'Brak zadań'}
                      </p>
                    </div>
                  ) : (
                    list.cards.map((card) => {
                      const progress = getCardProgress(card);
                      return (
                        <div
                          key={card.id}
                          draggable
                          onDragStart={(e) => {
                            // Tylko jeśli nie przeciągamy elementu checklista
                            e.stopPropagation();
                            handleKanbanCardDragStart(e, card, list.id);
                          }}
                          onDragEnd={handleKanbanCardDragEnd}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Jeśli przeciągamy element checklista, użyj istniejącej obsługi
                            if (draggedChecklistItem && !draggedKanbanCard) {
                              e.dataTransfer.dropEffect = 'move';
                              setDragOverCard(card.id);
                            }
                          }}
                          onDragLeave={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) {
                              setDragOverCard(null);
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            // Jeśli przeciągamy element checklista, użyj istniejącej obsługi
                            if (draggedChecklistItem && !draggedKanbanCard) {
                              const { item, sourceCardId, sourceChecklistId } = draggedChecklistItem;

                              // Jeśli element jest upuszczany na tę samą kartę, otwórz modal zamiast przenosić
                              if (sourceCardId === card.id) {
                                setSelectedCard(card);
                                setSelectedCardListId(list.id);
                                setDescriptionText(card.description || '');
                                setEditingDescription(false);
                                setDraggedChecklistItem(null);
                                setDragOverCard(null);
                                return;
                              }

                              // Znajdź źródłową kartę i listę
                              let sourceCard = null;
                              let sourceListId = null;
                              for (const listItem of kanbanLists) {
                                sourceCard = listItem.cards.find(c => c.id === sourceCardId);
                                if (sourceCard) {
                                  sourceListId = listItem.id;
                                  break;
                                }
                              }

                              if (!sourceCard) {
                                setDraggedChecklistItem(null);
                                setDragOverCard(null);
                                return;
                              }

                              // Znajdź lub utwórz checklist w docelowej karcie
                              let targetChecklistId = null;
                              const updatedTargetCard = { ...card };
                              
                              // Jeśli karta ma checklisty, użyj pierwszego
                              if (card.checklists && card.checklists.length > 0) {
                                targetChecklistId = card.checklists[0].id;
                              } else {
                                // Utwórz nowy checklist
                                const newChecklist = {
                                  id: Date.now().toString(),
                                  title: 'Zadania',
                                  items: []
                                };
                                updatedTargetCard.checklists = [newChecklist];
                                targetChecklistId = newChecklist.id;
                              }

                              // Usuń element ze źródłowego checklista
                              const updatedSourceCard = {
                                ...sourceCard,
                                checklists: (sourceCard.checklists || []).map(c =>
                                  c.id === sourceChecklistId
                                    ? {
                                        ...c,
                                        items: (c.items || []).filter(i => i.id !== item.id)
                                      }
                                    : c
                                )
                              };

                              // Dodaj element do docelowego checklista
                              updatedTargetCard.checklists = updatedTargetCard.checklists.map(c =>
                                c.id === targetChecklistId
                                  ? {
                                      ...c,
                                      items: [...(c.items || []), item]
                                    }
                                  : c
                              );

                              // Dodaj aktywność do obu kart
                              const sourceCardWithActivity = addActivity(updatedSourceCard, 'move', 
                                `przeniósł element "${item.title}" do "${card.title}"`
                              );
                              const targetCardWithActivity = addActivity(updatedTargetCard, 'move', 
                                `przeniósł element "${item.title}" z "${sourceCard.title}"`
                              );

                              // Aktualizuj listy
                              const updatedLists = kanbanLists.map(listItem => {
                                if (listItem.id === sourceListId) {
                                  return {
                                    ...listItem,
                                    cards: listItem.cards.map(c =>
                                      c.id === sourceCardId ? sourceCardWithActivity : c
                                    )
                                  };
                                }
                                if (listItem.id === list.id) {
                                  return {
                                    ...listItem,
                                    cards: listItem.cards.map(c =>
                                      c.id === card.id ? targetCardWithActivity : c
                                    )
                                  };
                                }
                                return listItem;
                              });

                              setKanbanLists(updatedLists);

                              // Jeśli otwarta karta była źródłem lub celem, zaktualizuj ją
                              if (selectedCard && (selectedCard.id === sourceCardId || selectedCard.id === card.id)) {
                                if (selectedCard.id === sourceCardId) {
                                  setSelectedCard(sourceCardWithActivity);
                                } else {
                                  setSelectedCard(targetCardWithActivity);
                                }
                              }

                              setDraggedChecklistItem(null);
                              setDragOverCard(null);
                              toast.success(`Element przeniesiony do "${card.title}"`);
                            }
                          }}
                          className={`bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all cursor-move shadow-sm ${
                            dragOverCard === card.id ? 'border-blue-400 bg-blue-50 shadow-md' : ''
                          } ${
                            draggedKanbanCard?.card.id === card.id ? 'opacity-50' : ''
                          }`}
                          onClick={(e) => {
                            // Pojedyncze kliknięcie - otwórz modal
                            setShowListMenu(null);
                            setSelectedCard(card);
                            setSelectedCardListId(list.id);
                            setDescriptionText(card.description || '');
                            setEditingDescription(false);
                          }}
                        >
                          {/* Tytuł karty */}
                          <p className="text-sm font-medium text-gray-900 mb-2 leading-snug">{card.title}</p>
                          
                          {/* Dolna sekcja z ikonkami i badge'ami */}
                          <div className="flex items-center justify-between mt-2 flex-wrap gap-1.5">
                            {/* Lewa strona - ikony i badge'e */}
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {/* Badge dla checklistów */}
                              {progress && progress.total > 0 && (
                                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-green-50 text-green-700 rounded-md text-xs font-medium">
                                  <CheckSquare className="h-3 w-3" />
                                  <span>{progress.completed}/{progress.total}</span>
                                </div>
                              )}
                              
                              {/* Ikona checklista (jeśli są checklisty) */}
                              {card.checklists && card.checklists.length > 0 && (
                                <div className="flex items-center text-gray-500 hover:text-gray-700">
                                  <List className="h-3.5 w-3.5" />
                                </div>
                              )}
                              
                              {/* Ikona komentarzy */}
                              {card.comments && card.comments.length > 0 && (
                                <div className="flex items-center gap-1 text-gray-500 hover:text-gray-700">
                                  <MessageSquare className="h-3.5 w-3.5" />
                                  <span className="text-xs font-medium">{card.comments.length}</span>
                                </div>
                              )}
                              
                              {/* Ikona opisu */}
                              {card.description && card.description.trim() && (
                                <div className="flex items-center text-gray-500 hover:text-gray-700">
                                  <FileText className="h-3.5 w-3.5" />
                                </div>
                              )}
                              
                              {/* Ikona obserwowania (można dodać w przyszłości) */}
                              <div className="flex items-center text-gray-400 hover:text-gray-600">
                                <Eye className="h-3.5 w-3.5" />
                              </div>
                            </div>
                            
                            {/* Prawa strona - avatar użytkownika */}
                            <div className="flex items-center">
                              <AvatarDisplay username={user?.username} size="w-6 h-6" />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                
                {/* Przycisk dodawania karty */}
                <button
                  onClick={() => {
                    setSelectedListId(list.id);
                    setNewCardTitle('');
                    setShowAddCardModal(true);
                  }}
                  className="w-full py-2.5 px-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 text-sm font-medium transition-colors border border-transparent hover:border-gray-200 shadow-sm"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Dodaj kartę
                </button>
              </div>
            ))}
            
            {/* Przycisk dodawania nowej listy */}
            <button
              onClick={() => setShowAddListModal(true)}
              className="flex-shrink-0 w-64 h-12 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center text-gray-700 text-sm font-medium transition-colors"
              style={{ minHeight: 'fit-content' }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Dodaj kolejną listę
            </button>
          </div>
        </div>
      )}

      {/* Modal do dodawania nowej listy */}
      {showAddListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Dodaj nową listę
              </h3>
              <button
                onClick={() => {
                  setShowAddListModal(false);
                  setNewListTitle('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newListTitle.trim()) {
                  createListMutation.mutate({
                    title: newListTitle.trim()
                  });
                  setNewListTitle('');
                  setShowAddListModal(false);
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nazwa listy
                </label>
                <input
                  type="text"
                  value={newListTitle}
                  onChange={(e) => setNewListTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="np. Do przeglądu"
                  autoFocus
                  required
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddListModal(false);
                    setNewListTitle('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Dodaj listę
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal do dodawania karty */}
      {showAddCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Dodaj kartę
              </h3>
              <button
                onClick={() => {
                  setShowAddCardModal(false);
                  setNewCardTitle('');
                  setSelectedListId(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newCardTitle.trim() && selectedListId) {
                  createCardMutation.mutate({
                    listId: selectedListId,
                    title: newCardTitle.trim(),
                    description: '',
                    checklists: [],
                    comments: [],
                    activities: []
                  });
                  setNewCardTitle('');
                  setSelectedListId(null);
                  setShowAddCardModal(false);
                  toast.success('Karta została dodana');
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tytuł karty
                </label>
                <input
                  type="text"
                  value={newCardTitle}
                  onChange={(e) => setNewCardTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Wpisz tytuł karty"
                  autoFocus
                  required
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCardModal(false);
                    setNewCardTitle('');
                    setSelectedListId(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Dodaj kartę
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal szczegółów karty */}
      {selectedCard && selectedCardListId && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full my-8" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
            {/* Header */}
            <div className="bg-gray-800 text-white px-6 py-3 rounded-t-lg flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    setSelectedCard(null);
                    setSelectedCardListId(null);
                    setShowListDropdown(false);
                    setEditingDescription(false);
                    setDescriptionText('');
                    setNewComment('');
                    setNewChecklistTitle('');
                    setNewChecklistItemTitle({});
                    setHiddenChecklists({});
                  }}
                  className="p-1.5 rounded hover:bg-gray-700 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <button
                onClick={() => {
                  setSelectedCard(null);
                  setSelectedCardListId(null);
                  setShowListDropdown(false);
                  setEditingDescription(false);
                  setDescriptionText('');
                  setNewComment('');
                  setNewChecklistTitle('');
                  setNewChecklistItemTitle({});
                  setHiddenChecklists({});
                  setDraggedChecklistItem(null);
                  setDragOverChecklist(null);
                  setDragOverCard(null);
                }}
                className="p-1.5 rounded hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-8 flex gap-8 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 8rem)' }}>
              {/* Lewa kolumna - szczegóły karty */}
              <div className="flex-1 min-w-0 space-y-6">
                {/* Lista karty */}
                <div className="relative">
                  <button 
                    onClick={() => setShowListDropdown(!showListDropdown)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 flex items-center transition-colors border border-gray-200"
                  >
                    {kanbanLists.find(l => l.id === selectedCardListId)?.title}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </button>
                  
                  {/* Dropdown z listami */}
                  {showListDropdown && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowListDropdown(false)}
                      />
                      <div className="absolute top-12 left-0 z-20 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px]">
                        {kanbanLists.map((list) => (
                          <button
                            key={list.id}
                            onClick={() => {
                              if (list.id !== selectedCardListId) {
                                // Usuń kartę z starej listy
                                const updatedLists = kanbanLists.map(l => {
                                  if (l.id === selectedCardListId) {
                                    return {
                                      ...l,
                                      cards: l.cards.filter(c => c.id !== selectedCard.id)
                                    };
                                  }
                                  return l;
                                });
                                
                                // Dodaj kartę do nowej listy
                                const finalLists = updatedLists.map(l => {
                                  if (l.id === list.id) {
                                    return {
                                      ...l,
                                      cards: [...l.cards, selectedCard]
                                    };
                                  }
                                  return l;
                                });
                                
                                setKanbanLists(finalLists);
                                setSelectedCardListId(list.id);
                                const updatedCardWithActivity = addActivity(selectedCard, 'move', `przeniósł kartę do "${list.title}"`);
                                setSelectedCard(updatedCardWithActivity);
                                setKanbanLists(finalLists.map(l =>
                                  l.id === list.id
                                    ? {
                                        ...l,
                                        cards: l.cards.map(c =>
                                          c.id === selectedCard.id ? updatedCardWithActivity : c
                                        )
                                      }
                                    : l
                                ));
                                toast.success(`Karta przeniesiona do "${list.title}"`);
                              }
                              setShowListDropdown(false);
                            }}
                            className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center justify-between ${
                              list.id === selectedCardListId
                                ? 'bg-blue-50 text-blue-700 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <span>{list.title}</span>
                            {list.id === selectedCardListId && (
                              <span className="text-blue-600">✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Tytuł karty */}
                <div className="mb-4">
                  {editingCardTitleInModal ? (
                    <input
                      type="text"
                      value={editingCardTitleTextInModal}
                      onChange={(e) => setEditingCardTitleTextInModal(e.target.value)}
                      onBlur={() => {
                        // Zapisz zmiany
                        if (editingCardTitleTextInModal.trim() && editingCardTitleTextInModal !== selectedCard.title) {
                          const updatedCard = {
                            ...selectedCard,
                            title: editingCardTitleTextInModal.trim()
                          };
                          const cardWithActivity = addActivity(updatedCard, 'title', `zmienił tytuł na "${editingCardTitleTextInModal.trim()}"`);
                          
                          setKanbanLists(kanbanLists.map(listItem =>
                            listItem.id === selectedCardListId
                              ? {
                                  ...listItem,
                                  cards: listItem.cards.map(c =>
                                    c.id === selectedCard.id ? cardWithActivity : c
                                  )
                                }
                              : listItem
                          ));
                          
                          setSelectedCard(cardWithActivity);
                          
                          // Zapisz przez API
                          updateCardMutation.mutate({
                            id: selectedCard.id,
                            data: {
                              title: cardWithActivity.title,
                              activities: cardWithActivity.activities || []
                            }
                          });
                          
                          toast.success('Nazwa karty została zapisana');
                        }
                        setEditingCardTitleInModal(false);
                        setEditingCardTitleTextInModal('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.target.blur();
                        }
                        if (e.key === 'Escape') {
                          setEditingCardTitleInModal(false);
                          setEditingCardTitleTextInModal('');
                        }
                      }}
                      autoFocus
                      className="w-full text-2xl font-bold text-gray-900 leading-tight px-2 py-1 border-2 border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                    />
                  ) : (
                    <h2 
                      className="text-2xl font-bold text-gray-900 leading-tight cursor-text hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                      onClick={() => {
                        setEditingCardTitleInModal(true);
                        setEditingCardTitleTextInModal(selectedCard.title);
                      }}
                    >
                      {selectedCard.title}
                    </h2>
                  )}
                </div>

                {/* Opis */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Opis</h3>
                    {!editingDescription && (
                      <button
                        onClick={() => {
                          setEditingDescription(true);
                          setDescriptionText(selectedCard.description || '');
                        }}
                        className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
                      >
                        {selectedCard.description ? 'Edytuj' : 'Dodaj'}
                      </button>
                    )}
                  </div>
                  
                  {editingDescription ? (
                    <div>
                      <textarea
                        value={descriptionText}
                        onChange={(e) => setDescriptionText(e.target.value)}
                        placeholder="Dodaj bardziej szczegółowy opis..."
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px] text-sm resize-none"
                        autoFocus
                      />
                      <div className="flex space-x-2 mt-3">
                        <button
                          onClick={() => {
                            const updatedCard = {
                              ...selectedCard,
                              description: descriptionText.trim()
                            };
                            const cardWithActivity = addActivity(updatedCard, 'description', 
                              descriptionText.trim() ? 'zaktualizował opis' : 'usunął opis'
                            );
                            setSelectedCard(cardWithActivity);
                            
                            // Zapisz przez API
                            updateCardMutation.mutate({
                              id: selectedCard.id,
                              data: {
                                description: cardWithActivity.description || '',
                                activities: cardWithActivity.activities || []
                              }
                            });
                            
                            setEditingDescription(false);
                            toast.success('Opis został zapisany');
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                        >
                          Zapisz
                        </button>
                        <button
                          onClick={() => {
                            setEditingDescription(false);
                            setDescriptionText('');
                          }}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm transition-colors"
                        >
                          Anuluj
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => {
                        setEditingDescription(true);
                        setDescriptionText(selectedCard.description || '');
                      }}
                      className={`px-4 py-3 rounded-lg border-2 transition-colors ${
                        selectedCard.description
                          ? 'border-gray-200 bg-white text-gray-900 cursor-text hover:border-gray-300'
                          : 'border-dashed border-gray-300 bg-gray-50 text-gray-400 cursor-pointer hover:bg-gray-100 hover:border-gray-400'
                      }`}
                    >
                      {selectedCard.description || 'Dodaj bardziej szczegółowy opis...'}
                    </div>
                  )}
                </div>

                {/* Checklisty */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Checklisty</h3>
                  
                  {/* Lista checklistów */}
                  <div className="space-y-4 mb-4">
                    {(selectedCard.checklists || []).map((checklist) => {
                      const progress = getChecklistProgress(checklist);
                      const visibleItems = hiddenChecklists[checklist.id] 
                        ? checklist.items.filter(item => !item.completed)
                        : (checklist.items || []);
                      const hasCompletedItems = checklist.items?.some(item => item.completed) || false;
                      
                      return (
                        <div key={checklist.id} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                          {/* Nagłówek checklista */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <CheckSquare className="h-5 w-5 text-gray-500" />
                              <h4 className="font-semibold text-gray-900">{checklist.title}</h4>
                              {progress.total > 0 && (
                                <span className="text-sm font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                                  {progress.percent}%
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {hasCompletedItems && (
                                <button
                                  onClick={() => {
                                    setHiddenChecklists(prev => ({
                                      ...prev,
                                      [checklist.id]: !prev[checklist.id]
                                    }));
                                  }}
                                  className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors"
                                >
                                  {hiddenChecklists[checklist.id] ? 'Pokaż zaznaczone' : 'Ukryj zaznaczone'}
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  if (window.confirm(`Czy na pewno chcesz usunąć checklist "${checklist.title}"?`)) {
                                    const updatedCard = {
                                      ...selectedCard,
                                      checklists: (selectedCard.checklists || []).filter(c => c.id !== checklist.id)
                                    };
                                    const cardWithActivity = addActivity(updatedCard, 'checklist', `usunął checklist "${checklist.title}"`);
                                    setSelectedCard(cardWithActivity);
                                    setKanbanLists(kanbanLists.map(list =>
                                      list.id === selectedCardListId
                                        ? {
                                            ...list,
                                            cards: list.cards.map(c =>
                                              c.id === selectedCard.id ? cardWithActivity : c
                                            )
                                          }
                                        : list
                                    ));
                                    toast.success('Checklist został usunięty');
                                  }
                                }}
                                className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50 transition-colors"
                              >
                                Usuń
                              </button>
                            </div>
                          </div>

                          {/* Pasek postępu */}
                          {progress.total > 0 && (
                            <div className="mb-4">
                              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-300 ${
                                    progress.percent === 100 ? 'bg-green-500' : 'bg-blue-500'
                                  }`}
                                  style={{ width: `${progress.percent}%` }}
                                />
                              </div>
                            </div>
                          )}

                          {/* Elementy checklista */}
                          <div 
                            className={`space-y-1.5 mb-3 min-h-[40px] transition-colors ${
                              dragOverChecklist === checklist.id && dragOverCard === selectedCard.id 
                                ? 'bg-blue-50 border-2 border-blue-300 border-dashed rounded-lg p-2' 
                                : ''
                            }`}
                            onDragOver={(e) => handleChecklistDragOver(e, checklist.id, selectedCard.id)}
                            onDragLeave={handleChecklistDragLeave}
                            onDrop={(e) => handleChecklistDrop(e, checklist.id, selectedCard.id)}
                          >
                            {visibleItems.length === 0 ? (
                              <div className="text-sm text-gray-400 italic py-2">
                                {progress.total > 0 && hiddenChecklists[checklist.id] 
                                  ? 'Wszystkie elementy są wykonane'
                                  : dragOverChecklist === checklist.id && dragOverCard === selectedCard.id
                                    ? 'Upuść tutaj'
                                    : 'Brak elementów'}
                              </div>
                            ) : (
                              visibleItems.map((item) => (
                                <div 
                                  key={item.id} 
                                  draggable
                                  onDragStart={(e) => handleChecklistItemDragStart(e, item, checklist.id, selectedCard.id)}
                                  onDragEnd={handleChecklistDragEnd}
                                  className={`flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-md transition-colors cursor-move ${
                                    draggedChecklistItem?.item.id === item.id && draggedChecklistItem?.sourceCardId === selectedCard.id && draggedChecklistItem?.sourceChecklistId === checklist.id
                                      ? 'opacity-50'
                                      : ''
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={item.completed || false}
                                    onChange={(e) => {
                                      const updatedCard = {
                                        ...selectedCard,
                                        checklists: (selectedCard.checklists || []).map(c =>
                                          c.id === checklist.id
                                            ? {
                                                ...c,
                                                items: (c.items || []).map(i =>
                                                  i.id === item.id ? { ...i, completed: e.target.checked } : i
                                                )
                                              }
                                            : c
                                        )
                                      };
                                      const cardWithActivity = addActivity(updatedCard, 'checklist', 
                                        e.target.checked 
                                          ? `oznaczył element "${item.title}" jako wykonane w "${checklist.title}"`
                                          : `cofnął oznaczenie elementu "${item.title}" w "${checklist.title}"`
                                      );
                                      setSelectedCard(cardWithActivity);
                                      
                                      // Zapisz przez API
                                      updateCardMutation.mutate({
                                        id: selectedCard.id,
                                        data: {
                                          checklists: cardWithActivity.checklists || [],
                                          activities: cardWithActivity.activities || []
                                        }
                                      });
                                    }}
                                    className="h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span
                                    className={`flex-1 text-sm ${
                                      item.completed ? 'line-through text-gray-400' : 'text-gray-900'
                                    }`}
                                  >
                                    {item.title}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const updatedCard = {
                                        ...selectedCard,
                                        checklists: (selectedCard.checklists || []).map(c =>
                                          c.id === checklist.id
                                            ? {
                                                ...c,
                                                items: (c.items || []).filter(i => i.id !== item.id)
                                              }
                                            : c
                                        )
                                      };
                                      const cardWithActivity = addActivity(updatedCard, 'checklist', `usunął element "${item.title}" z "${checklist.title}"`);
                                      setSelectedCard(cardWithActivity);
                                      
                                      // Zapisz przez API
                                      updateCardMutation.mutate({
                                        id: selectedCard.id,
                                        data: {
                                          checklists: cardWithActivity.checklists || [],
                                          activities: cardWithActivity.activities || []
                                        }
                                      });
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))
                            )}
                          </div>

                          {/* Formularz dodawania elementu */}
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              const itemTitle = newChecklistItemTitle[checklist.id]?.trim();
                              if (itemTitle) {
                                const newItem = {
                                  id: Date.now().toString(),
                                  title: itemTitle,
                                  completed: false
                                };
                                const updatedCard = {
                                  ...selectedCard,
                                  checklists: (selectedCard.checklists || []).map(c =>
                                    c.id === checklist.id
                                      ? {
                                          ...c,
                                          items: [...(c.items || []), newItem]
                                        }
                                      : c
                                  )
                                };
                                const cardWithActivity = addActivity(updatedCard, 'checklist', `dodał element "${newItem.title}" do "${checklist.title}"`);
                                setSelectedCard(cardWithActivity);
                                
                                // Zapisz przez API
                                updateCardMutation.mutate({
                                  id: selectedCard.id,
                                  data: {
                                    checklists: cardWithActivity.checklists || [],
                                    activities: cardWithActivity.activities || []
                                  }
                                });
                                
                                setNewChecklistItemTitle(prev => ({ ...prev, [checklist.id]: '' }));
                              }
                            }}
                            className="flex items-center space-x-2 mt-3 pt-3 border-t border-gray-200"
                          >
                            <Plus className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <input
                              type="text"
                              value={newChecklistItemTitle[checklist.id] || ''}
                              onChange={(e) => setNewChecklistItemTitle(prev => ({ ...prev, [checklist.id]: e.target.value }))}
                              placeholder="Dodaj element..."
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                            <button
                              type="submit"
                              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                            >
                              Dodaj
                            </button>
                          </form>
                        </div>
                      );
                    })}
                  </div>

                  {/* Formularz dodawania checklista */}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (newChecklistTitle.trim()) {
                        const newChecklist = {
                          id: Date.now().toString(),
                          title: newChecklistTitle.trim(),
                          items: []
                        };
                        const updatedCard = {
                          ...selectedCard,
                          checklists: [...(selectedCard.checklists || []), newChecklist]
                        };
                        const cardWithActivity = addActivity(updatedCard, 'checklist', `dodał checklist "${newChecklist.title}"`);
                        setSelectedCard(cardWithActivity);
                        
                        // Zapisz przez API
                        updateCardMutation.mutate({
                          id: selectedCard.id,
                          data: {
                            checklists: cardWithActivity.checklists || [],
                            activities: cardWithActivity.activities || []
                          }
                        });
                        
                        setNewChecklistTitle('');
                        toast.success('Checklist został dodany');
                      }
                    }}
                    className="flex items-center space-x-2"
                  >
                    <CheckSquare className="h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={newChecklistTitle}
                      onChange={(e) => setNewChecklistTitle(e.target.value)}
                      placeholder="Dodaj checklist..."
                      className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                    >
                      Dodaj
                    </button>
                  </form>
                </div>
              </div>

              {/* Prawa kolumna - komentarze i aktywność */}
              <div className="w-80 flex-shrink-0 border-l border-gray-200 pl-8">
                {/* Komentarze i aktywność */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center">
                    <MessageSquare className="h-4 w-4 text-gray-500 mr-2" />
                    Komentarze i aktywność
                  </h3>

                  {/* Formularz dodawania komentarza */}
                  <div className="mb-6">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Napisz komentarz..."
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px] text-sm resize-none"
                    />
                    <button
                      onClick={() => {
                        if (newComment.trim()) {
                          const comment = {
                            id: Date.now().toString(),
                            text: newComment.trim(),
                            author: user?.username || user?.email || 'Użytkownik',
                            createdAt: new Date().toISOString()
                          };
                          const updatedCard = {
                            ...selectedCard,
                            comments: [...(selectedCard.comments || []), comment]
                          };
                          const cardWithActivity = addActivity(updatedCard, 'comment', `dodał komentarz`);
                          setSelectedCard(cardWithActivity);
                          
                          // Zapisz przez API
                          updateCardMutation.mutate({
                            id: selectedCard.id,
                            data: {
                              comments: cardWithActivity.comments || [],
                              activities: cardWithActivity.activities || []
                            }
                          });
                          
                          setNewComment('');
                          toast.success('Komentarz został dodany');
                        }
                      }}
                      className="mt-3 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                    >
                      Zapisz
                    </button>
                  </div>

                  {/* Lista aktywności i komentarzy */}
                  <div className="space-y-4 max-h-[calc(100vh-450px)] overflow-y-auto">
                    {/* Komentarze */}
                    {(selectedCard.comments || []).map((comment) => (
                      <div key={comment.id} className="border-l-4 border-blue-400 pl-4 py-3 bg-blue-50 rounded-r-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <AvatarDisplay username={comment.author} size="w-6 h-6" />
                            <span className="text-sm font-semibold text-gray-900">{comment.author}</span>
                          </div>
                          <span className="text-xs text-gray-500">{formatActivityDate(comment.date)}</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{comment.text}</p>
                      </div>
                    ))}

                    {/* Aktywność */}
                    {(selectedCard.activity || []).slice().reverse().map((activity) => (
                      <div key={activity.id} className="flex items-start space-x-3 py-2">
                        <AvatarDisplay username={activity.author} size="w-8 h-8" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-600 leading-relaxed">
                            <span className="font-semibold text-gray-900">{activity.author}</span>{' '}
                            <span>{activity.text}</span>
                            <div className="text-xs text-gray-400 mt-1">{formatActivityDate(activity.date)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Widok: MAPA */}
      {activeSubmenu === 'map' && (
        <div className="card">
          <div className="card-header bg-gradient-to-r from-red-50 to-orange-50">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Mapa Polski
              </h3>
              {(user?.role === 'super admin' || user?.role === 'admin') && (
                <button
                  onClick={openAddFarmModal}
                  className="btn-primary flex items-center"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Dodaj farmę
                </button>
              )}
            </div>
          </div>
          <div className="card-body">
            {farmsError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-semibold">Błąd pobierania farm</p>
                <p className="text-red-600 text-sm mt-1">
                  {farmsError.response?.data?.message || farmsError.message || 'Nie udało się pobrać farm z serwera'}
                </p>
                {farmsError.response?.status === 403 && (
                  <p className="text-red-600 text-sm mt-2">
                    Brak uprawnień do modułu Mapa. Skontaktuj się z administratorem.
                  </p>
                )}
              </div>
            )}
            {farmsLoading && (
              <div className="text-center py-4 text-gray-500">
                <LoadingSpinner />
                <p className="mt-2">Ładowanie farm...</p>
              </div>
            )}
            <div ref={mapRef} style={{ width: '100%', height: '600px' }} className="rounded-lg border border-gray-300" />
            {!mapLoaded && !farmsLoading && (
              <div className="text-center py-4 text-gray-500">
                <p>Ładowanie mapy...</p>
              </div>
            )}
            {mapLoaded && !farmsLoading && farms.length === 0 && !farmsError && (
              <div className="text-center py-4 text-gray-500">
                <p>Brak farm do wyświetlenia na mapie.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal do dodawania/edycji ekipy */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingTeam ? 'Edytuj ekipę' : 'Dodaj ekipę'}
              </h3>
              <button
                onClick={() => {
                  setShowTeamModal(false);
                  setEditingTeam(null);
                  resetTeamForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleTeamSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nazwa Ekipy
                </label>
                <input
                  type="text"
                  value={teamFormData.teamName}
                  onChange={(e) => setTeamFormData({ ...teamFormData, teamName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Wpisz nazwę ekipy"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pierwszy serwisant <span className="text-red-500">*</span>
                </label>
                {usersLoading ? (
                  <div className="px-3 py-2 border border-gray-300 rounded-lg">
                    <span className="text-gray-500">Ładowanie użytkowników...</span>
                  </div>
                ) : (
                  <select
                    value={teamFormData.firstServicePerson}
                    onChange={(e) => setTeamFormData({ ...teamFormData, firstServicePerson: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Wybierz użytkownika</option>
                    {allUsers.map(u => (
                      <option key={u._id} value={u._id}>
                        {u.username} {u.email && `(${u.email})`}
                      </option>
                    ))}
                  </select>
                )}
                {allUsers.length === 0 && !usersLoading && (
                  <p className="mt-1 text-xs text-orange-600">
                    Brak użytkowników w systemie. Najpierw dodaj użytkowników w sekcji "Użytkownicy".
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Drugi serwisant (nieobowiązkowe)
                </label>
                {usersLoading ? (
                  <div className="px-3 py-2 border border-gray-300 rounded-lg">
                    <span className="text-gray-500">Ładowanie użytkowników...</span>
                  </div>
                ) : (
                  <select
                    value={teamFormData.secondServicePerson}
                    onChange={(e) => setTeamFormData({ ...teamFormData, secondServicePerson: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Brak (tylko pierwszy serwisant)</option>
                    {allUsers
                      .filter(u => u._id !== teamFormData.firstServicePerson)
                      .map(u => (
                        <option key={u._id} value={u._id}>
                          {u.username} {u.email && `(${u.email})`}
                        </option>
                      ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kolor ekipy
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={teamFormData.color}
                    onChange={(e) => setTeamFormData({ ...teamFormData, color: e.target.value })}
                    className="h-10 w-20 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={teamFormData.color}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Automatycznie dodaj # jeśli brakuje
                      if (value && !value.startsWith('#')) {
                        value = '#' + value;
                      }
                      // Konwertuj na wielkie litery dla konsystencji
                      value = value.toUpperCase();
                      setTeamFormData({ ...teamFormData, color: value });
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="#3B82F6"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    title="Format: #RRGGBB (np. #3B82F6)"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Wybierz kolor, który będzie wyświetlany w kalendarzu dla zadań tej ekipy
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowTeamModal(false);
                    setEditingTeam(null);
                    resetTeamForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={addTeamMutation.isLoading || updateTeamMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {addTeamMutation.isLoading || updateTeamMutation.isLoading
                    ? 'Zapisywanie...'
                    : editingTeam
                    ? 'Zapisz zmiany'
                    : 'Dodaj ekipę'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal do dodawania/edycji farmy */}
      {showFarmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingFarm ? 'Edytuj farmę' : 'Dodaj farmę'}
              </h3>
              <button
                onClick={() => {
                  setShowFarmModal(false);
                  setEditingFarm(null);
                  resetFarmForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleFarmSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nazwa Farmy
                </label>
                <input
                  type="text"
                  value={farmFormData.farmName}
                  onChange={(e) => setFarmFormData({ ...farmFormData, farmName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Wpisz nazwę farmy"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lokalizacja GPS Farmy - Szerokość geograficzna (Latitude)
                </label>
                <input
                  type="number"
                  step="any"
                  value={farmFormData.latitude}
                  onChange={(e) => setFarmFormData({ ...farmFormData, latitude: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="np. 52.237049"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Wartość musi być między -90 a 90
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lokalizacja GPS Farmy - Długość geograficzna (Longitude)
                </label>
                <input
                  type="number"
                  step="any"
                  value={farmFormData.longitude}
                  onChange={(e) => setFarmFormData({ ...farmFormData, longitude: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="np. 21.017532"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Wartość musi być między -180 a 180
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowFarmModal(false);
                    setEditingFarm(null);
                    resetFarmForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={addFarmMutation.isLoading || updateFarmMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {addFarmMutation.isLoading || updateFarmMutation.isLoading
                    ? 'Zapisywanie...'
                    : editingFarm
                    ? 'Zapisz zmiany'
                    : 'Dodaj farmę'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Planner;
